<template>
  <Admin style="background: #f2f2f2">
    <v-main>
      <v-container>
        <v-card class="rounded-xl tw-min-h-80" v-if="!isProductListLoading">
          <v-card-text v-if="!selectedCompany.isDemo">
            <v-breadcrumbs :items="items">
              <template v-slot:divider>
                <v-icon>mdi-chevron-right</v-icon>
              </template>
              <template v-slot:item="{ item }">
                <v-breadcrumbs-item
                  :to="item.to"
                  :disabled="item.disabled"
                  :exact="true"
                >
                  {{ item.text }}
                </v-breadcrumbs-item>
              </template>
            </v-breadcrumbs>

            <h2 class="tw-text-xl tw-font-bold tw-text-black tw-mb-6 tw-px-4">
              สร้างสำเนาบริษัทและตั้งค่า
            </h2>

            <v-form ref="form" v-model="formValid" @submit.prevent="runSubmit">
            <v-container fluid class="pa-4">
              <!-- ============== COMPANY CONFIG ============== -->
              <v-card outlined class="rounded-xl tw-mb-6">
                <v-card-title class="tw-text-base tw-font-bold">
                  Company Config
                </v-card-title>
                <v-divider />
                <v-card-text>
                  <v-row dense>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.companyId"
                        label="Company"
                        outlined
                        dense
                        readonly
                        prepend-inner-icon="mdi-domain"
                      />
                    </v-col>

                    <v-col cols="12" md="3">
                      <v-text-field
                        v-model.number="form.copyFrom"
                        label="Copy From No."
                        type="number"
                        min="1"
                        outlined
                        dense
                        prepend-inner-icon="mdi-numeric-1-box-outline"
                        :rules="[rules.required, rules.minOne]"
                      />
                    </v-col>

                    <v-col cols="12" md="3">
                      <v-text-field
                        v-model.number="form.copyTo"
                        label="Copy To No."
                        type="number"
                        min="1"
                        outlined
                        dense
                        prepend-inner-icon="mdi-numeric-9-plus-box-outline"
                        :rules="[rules.required, rules.endGteStart]"
                      />
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.namePattern"
                        label="Name Pattern"
                        hint="Use {n} placeholder. Example: {n:3} → 001, 002, 003"
                        persistent-hint
                        outlined
                        dense
                        prepend-inner-icon="mdi-format-text"
                        :rules="[rules.required, rules.patternHasN]"
                      />
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-file-input
                        v-model="form.logoFile"
                        label="Company Logo"
                        accept="image/*"
                        outlined
                        dense
                        show-size
                        prepend-icon=""
                        prepend-inner-icon="mdi-image"
                        :rules="[rules.imageOnly, rules.imageMaxSize]"
                        @change="onLogoSelected"
                      />
                      <div v-if="logoPreview" class="tw-mt-2">
                        <img
                          :src="logoPreview"
                          alt="logo preview"
                          style="max-height: 80px; max-width: 100%; border-radius: 8px; border: 1px solid #ddd;"
                        />
                      </div>
                    </v-col>

                    <v-col cols="12" v-if="namePreview.length">
                      <div class="tw-text-xs tw-text-gray-500 tw-mb-1">
                        Sample names ({{ form.copyFrom }} → {{ form.copyTo }}):
                      </div>
                      <div class="tw-flex tw-flex-wrap tw-gap-2">
                        <v-chip
                          v-for="(name, idx) in namePreview"
                          :key="idx"
                          small
                          color="blue-grey lighten-4"
                          text-color="black"
                        >
                          {{ name }}
                        </v-chip>
                        <v-chip
                          v-if="namePreviewTruncated"
                          small
                          color="grey lighten-3"
                          text-color="grey darken-2"
                        >
                          ... +{{ totalCount - namePreview.length }} more
                        </v-chip>
                      </div>
                    </v-col>

                    <v-col cols="12">
                      <v-combobox
                        v-model="form.emails"
                        label="Email"
                        hint="Press Enter or use comma to add multiple emails"
                        persistent-hint
                        multiple
                        chips
                        deletable-chips
                        outlined
                        dense
                        :delimiters="[',', ';', ' ']"
                        :search-input.sync="emailInput"
                        :error-messages="emailErrors"
                        @input="onEmailsChanged"
                      >
                        <template v-slot:selection="{ attrs, item, parent }">
                          <v-chip
                            v-bind="attrs"
                            :color="chipColor(item)"
                            text-color="white"
                            small
                            close
                            @click:close="parent.selectItem(item)"
                          >
                            <v-icon
                              v-if="emailStatus[item] === 'checking'"
                              left
                              small
                            >
                              mdi-loading mdi-spin
                            </v-icon>
                            <v-icon
                              v-else-if="emailStatus[item] === 'registered'"
                              left
                              small
                            >
                              mdi-check-circle
                            </v-icon>
                            <v-icon
                              v-else-if="emailStatus[item] === 'not-registered'"
                              left
                              small
                            >
                              mdi-alert-circle
                            </v-icon>
                            <v-icon
                              v-else-if="emailStatus[item] === 'invalid'"
                              left
                              small
                            >
                              mdi-email-alert
                            </v-icon>
                            {{ item }}
                          </v-chip>
                        </template>
                      </v-combobox>
                    </v-col>

                    <v-col v-if="form.emails.length" cols="12">
                      <div class="tw-text-xs tw-font-bold tw-text-gray-700 tw-mb-2">
                        บทบาท (Role) ต่อ email
                      </div>
                      <div class="tw-flex tw-flex-col tw-gap-2">
                        <div
                          v-for="email in form.emails"
                          :key="email"
                          class="tw-flex tw-items-center tw-gap-3"
                        >
                          <span
                            class="tw-flex-1 tw-font-mono tw-text-xs tw-truncate"
                          >
                            {{ email }}
                          </span>
                          <v-select
                            :value="form.emailRoles[email] || 'owner'"
                            :items="roleOptions"
                            dense
                            outlined
                            hide-details
                            style="max-width: 200px"
                            @input="setEmailRole(email, $event)"
                          />
                        </div>
                      </div>
                      <div class="tw-text-xs tw-text-gray-500 tw-mt-2">
                        <strong>owner</strong>: เข้าถึงเต็ม (admin) ·
                        <strong>developer</strong>: เหมือน owner + SLD admin ·
                        <strong>user</strong>: อ่าน/เขียน docs เท่านั้น
                      </div>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <!-- ============== FOXPRO CONFIG ============== -->
              <v-card outlined class="rounded-xl tw-mb-6">
                <v-card-title class="tw-text-base tw-font-bold tw-flex tw-items-center tw-justify-between tw-gap-2">
                  <span class="tw-flex tw-items-center tw-gap-2">
                    FoxPro Config
                    <v-chip
                      v-if="foxproDefaultsLoaded"
                      x-small
                      color="green"
                      text-color="white"
                    >
                      <v-icon left x-small>mdi-cloud-download</v-icon>
                      Loaded defaults
                    </v-chip>
                    <v-progress-circular
                      v-if="isLoadingFoxproDefaults"
                      indeterminate
                      size="16"
                      width="2"
                      color="primary"
                    />
                  </span>
                  <v-btn
                    small
                    text
                    color="primary"
                    :loading="isLoadingFoxproDefaults"
                    @click="loadFoxproDefaults"
                  >
                    <v-icon left small>mdi-refresh</v-icon>
                    Reload Defaults
                  </v-btn>
                </v-card-title>
                <v-divider />
                <v-card-text>
                  <v-row dense>
                    <v-col cols="12">
                      <v-text-field
                        v-model="foxpro.apiUrl"
                        label="API URL"
                        placeholder="https://fox-acme.trycloudflare.com"
                        outlined
                        dense
                        prepend-inner-icon="mdi-link-variant"
                        :rules="[rules.required, rules.urlValid]"
                      />
                    </v-col>

                    <!-- ===== SMB MODE (only mode supported) ===== -->
                    <v-col cols="12" md="6">
                        <v-text-field
                          v-model="foxpro.networkPath"
                          label="Network Path"
                          placeholder="\\10.1.1.17"
                          outlined
                          dense
                          prepend-inner-icon="mdi-lan"
                          :rules="[rules.required]"
                        />
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="foxpro.shareName"
                          label="Share Name"
                          placeholder="expresi01"
                          outlined
                          dense
                          prepend-inner-icon="mdi-folder-network"
                          :rules="[rules.required]"
                        />
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="foxpro.username"
                          label="Username"
                          outlined
                          dense
                          prepend-inner-icon="mdi-account"
                          autocomplete="off"
                          :rules="[rules.required]"
                        />
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="foxpro.password"
                          label="Password"
                          outlined
                          dense
                          prepend-inner-icon="mdi-lock"
                          :type="showPassword ? 'text' : 'password'"
                          :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                          @click:append="showPassword = !showPassword"
                          autocomplete="new-password"
                          :rules="[rules.required]"
                        />
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="foxpro.domain"
                          label="Domain"
                          placeholder="WORKGROUP"
                          outlined
                          dense
                          prepend-inner-icon="mdi-domain"
                        />
                      </v-col>
                      <v-col cols="12">
                        <div class="tw-text-sm tw-font-bold tw-mb-2 tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-2">
                          <span>
                            Base Path
                            <span class="tw-text-xs tw-text-gray-500 tw-font-normal">
                              (one per copy: {{ form.copyFrom }} → {{ form.copyTo }}, total {{ totalCount }})
                            </span>
                          </span>

                          <!-- ===== Mode toggle when > 10 ===== -->
                          <div
                            v-if="totalCount > 10"
                            class="tw-flex tw-items-center tw-gap-2"
                          >
                            <v-btn
                              small
                              outlined
                              color="primary"
                              @click="downloadBasePathTemplate"
                            >
                              <v-icon small left>mdi-download</v-icon>
                              Download Template
                            </v-btn>

                            <input
                              ref="basePathFileInput"
                              type="file"
                              accept=".csv,text/csv"
                              class="d-none"
                              @change="onBasePathImport"
                            />
                            <v-btn
                              small
                              color="primary"
                              depressed
                              @click="triggerBasePathImport"
                            >
                              <v-icon small left>mdi-upload</v-icon>
                              Import CSV
                            </v-btn>

                            <v-btn-toggle
                              v-model="basePathMode"
                              dense
                              mandatory
                              color="primary"
                            >
                              <v-btn small value="import">
                                <v-icon small>mdi-file-table</v-icon>
                              </v-btn>
                              <v-btn small value="manual">
                                <v-icon small>mdi-form-textbox</v-icon>
                              </v-btn>
                            </v-btn-toggle>
                          </div>
                        </div>

                        <!-- ===== IMPORT SUMMARY (when > 10 and import mode) ===== -->
                        <v-alert
                          v-if="totalCount > 10 && basePathMode === 'import'"
                          type="info"
                          outlined
                          dense
                          class="tw-mt-2"
                        >
                          เนื่องจากมี {{ totalCount }} รายการ
                          กรุณา <strong>ดาวน์โหลด Template</strong> แล้ว <strong>Import CSV</strong>
                          หรือกดเปลี่ยนโหมดเป็น Manual เพื่อกรอกทีละช่อง
                          <span v-if="basePathFilledCount > 0" class="tw-block tw-mt-1">
                            <v-icon small color="green">mdi-check</v-icon>
                            กรอกแล้ว {{ basePathFilledCount }} / {{ totalCount }} รายการ
                          </span>
                        </v-alert>

                        <!-- ===== MANUAL INPUTS ===== -->
                        <v-row
                          v-if="copyNumbers.length && (totalCount <= 10 || basePathMode === 'manual')"
                          dense
                        >
                          <v-col
                            v-for="n in visibleCopyNumbers"
                            :key="n"
                            cols="12"
                            md="6"
                          >
                            <v-text-field
                              :value="foxpro.basePaths[n]"
                              :label="`Base Path #${n} (${formatName(form.namePattern, n)})`"
                              placeholder="DB/DATA"
                              outlined
                              dense
                              prepend-inner-icon="mdi-folder-open"
                              :rules="[rules.required]"
                              @input="setBasePath(n, $event)"
                            />
                          </v-col>

                          <v-col
                            v-if="totalCount > 10 && basePathMode === 'manual' && manualVisibleCount < totalCount"
                            cols="12"
                          >
                            <v-btn
                              outlined
                              color="primary"
                              block
                              @click="addMoreManualRows"
                            >
                              <v-icon left>mdi-plus</v-icon>
                              Add More Rows ({{ manualVisibleCount }} / {{ totalCount }})
                            </v-btn>
                          </v-col>
                        </v-row>

                        <v-alert
                          v-if="!copyNumbers.length"
                          type="info"
                          outlined
                          dense
                          class="tw-mt-2"
                        >
                          กรุณาตั้งค่า Copy From No. และ Copy To No. ก่อน
                        </v-alert>
                      </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <!-- ============== RUN STEPPER ============== -->
              <v-card
                v-if="runStarted"
                class="run-stepper-card rounded-xl tw-mb-6"
                :class="runStepperStateClass"
                elevation="3"
              >
                <!-- Hero header -->
                <div class="run-stepper-hero">
                  <div class="tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-3">
                    <div class="tw-flex tw-items-center tw-gap-3">
                      <v-avatar size="44" class="run-stepper-hero-icon">
                        <v-icon color="white" size="26">{{ runStepperHeroIcon }}</v-icon>
                      </v-avatar>
                      <div>
                        <div class="tw-text-white tw-font-bold tw-text-lg tw-leading-tight">
                          ขั้นตอนการดำเนินการ
                        </div>
                        <div class="tw-text-white tw-text-xs tw-opacity-80">
                          Step {{ currentStep }} of {{ steps.length }} · {{ runStepperHeroSubtitle }}
                        </div>
                      </div>
                    </div>
                    <v-chip
                      class="tw-text-white run-stepper-hero-chip"
                      label
                      small
                    >
                      <v-icon left small>mdi-progress-clock</v-icon>
                      {{ overallProgressPct }}%
                    </v-chip>
                  </div>
                  <v-progress-linear
                    :value="overallProgressPct"
                    height="6"
                    rounded
                    background-color="#e7edf5"
                    color="primary"
                    class="tw-mt-3"
                  />
                </div>

                <div class="run-stepper-metrics">
                  <div class="run-stepper-metric">
                    <span>Copies</span>
                    <strong>{{ totalCount }}</strong>
                  </div>
                  <div class="run-stepper-metric">
                    <span>Checked</span>
                    <strong>
                      {{ testProgressDone || testResults.length }} / {{ testProgressTotal || totalCount }}
                    </strong>
                  </div>
                  <div class="run-stepper-metric">
                    <span>Passed</span>
                    <strong class="is-ok">{{ testOkCount }}</strong>
                  </div>
                  <div class="run-stepper-metric">
                    <span>Failed</span>
                    <strong class="is-fail">{{ testFailCount }}</strong>
                  </div>
                </div>

                <div class="run-stepper-track">
                  <div
                    v-for="(s, idx) in steps"
                    :key="s.id"
                    class="run-stepper-track-item"
                    :class="[
                      stepItemClass(s),
                      {
                        'is-current': currentStep === s.id,
                        'is-last': idx === steps.length - 1,
                      },
                    ]"
                  >
                    <div class="run-stepper-step-marker">
                      <v-progress-circular
                        v-if="stepStatuses[s.id] === 'running'"
                        indeterminate
                        size="20"
                        width="2"
                        color="primary"
                      />
                      <v-icon v-else size="20">{{ stepStatusIcon(s) }}</v-icon>
                    </div>
                    <div class="run-stepper-step-copy">
                      <div class="run-stepper-step-title">{{ s.title }}</div>
                      <div class="run-stepper-step-desc">{{ s.description }}</div>
                    </div>
                    <v-chip
                      x-small
                      label
                      class="run-stepper-step-chip"
                      :class="stepItemClass(s)"
                    >
                      {{ stepStatusLabel(s) }}
                    </v-chip>
                  </div>
                </div>

                <v-stepper v-model="currentStep" flat class="run-stepper-body">
                  <v-stepper-header class="run-stepper-header">
                    <!-- eslint-disable vue/no-v-for-template-key-on-child -->
                    <template v-for="(s, idx) in steps">
                      <v-stepper-step
                        :key="`step-${s.id}`"
                        :step="s.id"
                        :complete="stepStatuses[s.id] === 'success'"
                        :complete-icon="s.icon || 'mdi-check'"
                        :edit-icon="s.icon || 'mdi-pencil'"
                        :rules="[() => stepStatuses[s.id] !== 'error']"
                        :color="stepStatuses[s.id] === 'success' ? 'green' : (stepStatuses[s.id] === 'running' ? 'primary' : 'grey')"
                        :editable="false"
                      >
                        <span class="tw-font-semibold">{{ s.title }}</span>
                        <v-chip
                          v-if="stepStatuses[s.id] === 'error'"
                          x-small
                          color="red"
                          text-color="white"
                          class="tw-mt-1"
                        >
                          <v-icon left x-small>mdi-alert</v-icon>
                          ล้มเหลว
                        </v-chip>
                        <v-chip
                          v-else-if="stepStatuses[s.id] === 'running'"
                          x-small
                          color="primary"
                          text-color="white"
                          class="tw-mt-1"
                        >
                          <v-progress-circular
                            indeterminate
                            size="10"
                            width="2"
                            color="white"
                            class="tw-mr-1"
                          />
                          กำลังทำงาน
                        </v-chip>
                        <v-chip
                          v-else-if="stepStatuses[s.id] === 'success'"
                          x-small
                          color="green"
                          text-color="white"
                          class="tw-mt-1"
                        >
                          <v-icon left x-small>mdi-check-circle</v-icon>
                          สำเร็จ
                        </v-chip>
                        <v-chip
                          v-else
                          x-small
                          color="grey lighten-2"
                          text-color="grey darken-2"
                          class="tw-mt-1"
                        >
                          รอดำเนินการ
                        </v-chip>
                      </v-stepper-step>
                      <v-divider
                        v-if="idx < steps.length - 1"
                        :key="`divider-${s.id}`"
                        class="run-stepper-divider"
                      />
                    </template>
                    <!-- eslint-enable vue/no-v-for-template-key-on-child -->
                  </v-stepper-header>

                  <v-stepper-items>
                    <!-- ===== Step 1: Test FoxPro Connection ===== -->
                    <v-stepper-content :step="1">
                      <transition name="fade-slide" mode="out-in">
                        <div :key="stepStatuses[1]">
                          <!-- Running state -->
                          <div
                            v-if="stepStatuses[1] === 'running' && testProgressTotal > 0"
                            class="run-stepper-status running tw-mb-4"
                          >
                            <div class="tw-flex tw-items-center tw-gap-3 tw-mb-3">
                              <v-progress-circular
                                indeterminate
                                size="28"
                                width="3"
                                color="primary"
                              />
                              <div>
                                <div class="tw-font-bold tw-text-base">
                                  กำลังทดสอบการเชื่อมต่อ FoxPro
                                </div>
                                <div class="tw-text-xs tw-text-gray-500">
                                  ตรวจสอบ {{ testProgressDone }} / {{ testProgressTotal }} base paths
                                </div>
                              </div>
                            </div>
                            <v-progress-linear
                              :value="(testProgressDone / testProgressTotal) * 100"
                              color="primary"
                              height="10"
                              rounded
                              striped
                            />
                          </div>

                          <!-- Error state -->
                          <div
                            v-else-if="testError || stepStatuses[1] === 'error'"
                            class="run-stepper-status error tw-mb-4"
                          >
                            <div class="tw-flex tw-items-center tw-gap-3">
                              <v-avatar size="44" color="red lighten-5">
                                <v-icon color="red" size="26">mdi-close-circle</v-icon>
                              </v-avatar>
                              <div>
                                <div class="tw-font-bold tw-text-base tw-text-red-600">
                                  ทดสอบเชื่อมต่อล้มเหลว
                                </div>
                                <div class="tw-text-sm tw-text-gray-700">
                                  <template v-if="testError">{{ testError }}</template>
                                  <template v-else>
                                    {{ testResults.length - testOkCount }} / {{ testResults.length }} base paths ล้มเหลว
                                    — กรุณากด <strong>Cancel</strong> เพื่อกลับไปแก้ไข
                                  </template>
                                </div>
                              </div>
                            </div>
                          </div>

                          <!-- Success state -->
                          <div
                            v-else-if="stepStatuses[1] === 'success'"
                            class="run-stepper-status success tw-mb-4"
                          >
                            <div class="tw-flex tw-items-center tw-gap-3">
                              <v-avatar size="44" color="green lighten-5">
                                <v-icon color="green" size="26">mdi-check-decagram</v-icon>
                              </v-avatar>
                              <div>
                                <div class="tw-font-bold tw-text-base tw-text-green-700">
                                  เชื่อมต่อ FoxPro สำเร็จ
                                </div>
                                <div class="tw-text-sm tw-text-gray-700">
                                  ทดสอบผ่านทั้งหมด {{ testOkCount }} / {{ testResults.length }} base paths
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </transition>

                      <div
                        v-if="testGatewayInfo && (testGatewayInfo.version || testGatewayInfo.buildId)"
                        class="tw-text-xs tw-text-gray-500 tw-mb-3 tw-flex tw-items-center tw-gap-2"
                      >
                        <v-icon x-small>mdi-server-network</v-icon>
                        Gateway
                        <span v-if="testGatewayInfo.version">v{{ testGatewayInfo.version }}</span>
                        <span v-if="testGatewayInfo.buildId">· build {{ testGatewayInfo.buildId }}</span>
                      </div>

                      <div
                        v-if="testResults.length"
                        class="run-stepper-results-panel"
                      >
                        <div class="run-stepper-results-toolbar">
                          <div>
                            <div class="run-stepper-results-title">Connection results</div>
                            <div class="run-stepper-results-subtitle">
                              {{ testOkCount }} passed, {{ testFailCount }} failed
                            </div>
                          </div>
                          <v-chip small label class="run-stepper-results-chip">
                            {{ testResults.length }} rows
                          </v-chip>
                        </div>
                        <div class="run-stepper-table-wrap">
                          <v-simple-table dense>
                            <thead>
                              <tr class="run-stepper-table-head">
                                <th style="width:60px">No.</th>
                                <th>Base Path</th>
                                <th style="width:110px">Status</th>
                                <th style="width:90px">Latency</th>
                                <th style="width:90px">Tables</th>
                                <th>Detail</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr
                                v-for="r in testResults"
                                :key="r.no"
                                :class="r.ok ? 'run-stepper-row-ok' : 'run-stepper-row-fail'"
                              >
                                <td class="tw-font-bold">{{ r.no }}</td>
                                <td class="run-stepper-base-path">{{ r.basePath }}</td>
                                <td>
                                  <v-chip
                                    small
                                    :color="r.ok ? 'green' : 'red'"
                                    text-color="white"
                                    class="tw-font-bold"
                                  >
                                    <v-icon left small>
                                      {{ r.ok ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                                    </v-icon>
                                    {{ r.ok ? 'OK' : 'FAIL' }}
                                  </v-chip>
                                </td>
                                <td class="tw-text-xs">{{ r.latencyMs }} ms</td>
                                <td>
                                  <span v-if="r.ok" class="tw-font-bold">{{ r.tableCount }}</span>
                                  <span v-else>-</span>
                                </td>
                                <td class="tw-text-xs">
                                  <span v-if="r.ok && r.sampleTables && r.sampleTables.length" class="tw-text-gray-500">
                                    e.g. {{ r.sampleTables.slice(0, 3).join(', ') }}
                                  </span>
                                  <span v-else-if="!r.ok" class="tw-text-red-600">
                                    {{ r.code }}<span v-if="r.error">: {{ r.error }}</span>
                                  </span>
                                </td>
                              </tr>
                            </tbody>
                          </v-simple-table>
                        </div>
                      </div>

                      <div class="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-mt-5">
                        <v-btn
                          v-if="stepStatuses[1] === 'error'"
                          color="grey darken-2"
                          outlined
                          rounded
                          @click="cancelRun"
                        >
                          <v-icon left>mdi-close</v-icon>
                          Cancel
                        </v-btn>
                        <v-btn
                          v-if="stepStatuses[1] === 'success'"
                          color="primary"
                          depressed
                          rounded
                          large
                          @click="goToStep(2)"
                        >
                          Continue
                          <v-icon right>mdi-chevron-right</v-icon>
                        </v-btn>
                      </div>
                    </v-stepper-content>

                    <!-- ===== Step 2: Read Company Info (ISINFO) ===== -->
                    <v-stepper-content :step="2">
                      <transition name="fade-slide" mode="out-in">
                        <div :key="stepStatuses[2]">
                          <!-- Running state -->
                          <div
                            v-if="stepStatuses[2] === 'running' && infoProgressTotal > 0"
                            class="run-stepper-status running tw-mb-4"
                          >
                            <div class="tw-flex tw-items-center tw-gap-3 tw-mb-3">
                              <v-progress-circular
                                indeterminate
                                size="28"
                                width="3"
                                color="primary"
                              />
                              <div>
                                <div class="tw-font-bold tw-text-base">
                                  กำลังอ่านข้อมูลบริษัท (ISINFO)
                                </div>
                                <div class="tw-text-xs tw-text-gray-500">
                                  อ่าน {{ infoProgressDone }} / {{ infoProgressTotal }} base paths
                                </div>
                              </div>
                            </div>
                            <v-progress-linear
                              :value="(infoProgressDone / infoProgressTotal) * 100"
                              color="primary"
                              height="10"
                              rounded
                              striped
                            />
                          </div>

                          <!-- Error state -->
                          <div
                            v-else-if="infoError || stepStatuses[2] === 'error'"
                            class="run-stepper-status error tw-mb-4"
                          >
                            <div class="tw-flex tw-items-center tw-gap-3">
                              <v-avatar size="44" color="red lighten-5">
                                <v-icon color="red" size="26">mdi-close-circle</v-icon>
                              </v-avatar>
                              <div>
                                <div class="tw-font-bold tw-text-base tw-text-red-600">
                                  อ่านข้อมูลบริษัทล้มเหลว
                                </div>
                                <div class="tw-text-sm tw-text-gray-700">
                                  <template v-if="infoError">{{ infoError }}</template>
                                  <template v-else>
                                    {{ infoResults.length - infoOkCount }} / {{ infoResults.length }} base paths ล้มเหลว
                                    — กรุณากด <strong>Cancel</strong> เพื่อกลับไปแก้ไข
                                  </template>
                                </div>
                              </div>
                            </div>
                          </div>

                          <!-- Success state -->
                          <div
                            v-else-if="stepStatuses[2] === 'success'"
                            class="run-stepper-status success tw-mb-4"
                          >
                            <div class="tw-flex tw-items-center tw-gap-3">
                              <v-avatar size="44" color="green lighten-5">
                                <v-icon color="green" size="26">mdi-check-decagram</v-icon>
                              </v-avatar>
                              <div>
                                <div class="tw-font-bold tw-text-base tw-text-green-700">
                                  อ่านข้อมูลบริษัทสำเร็จ
                                </div>
                                <div class="tw-text-sm tw-text-gray-700">
                                  อ่านสำเร็จ {{ infoOkCount }} / {{ infoResults.length }} base paths
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </transition>

                      <div
                        v-if="infoResults.length"
                        class="run-stepper-results-panel"
                      >
                        <div class="run-stepper-results-toolbar">
                          <div>
                            <div class="run-stepper-results-title">Company info (ISINFO)</div>
                            <div class="run-stepper-results-subtitle">
                              {{ infoOkCount }} read, {{ infoFailCount }} failed
                            </div>
                          </div>
                          <v-chip small label class="run-stepper-results-chip">
                            {{ infoResults.length }} rows
                          </v-chip>
                        </div>
                        <div class="run-stepper-table-wrap">
                          <v-simple-table dense>
                            <thead>
                              <tr class="run-stepper-table-head">
                                <th style="width:60px">No.</th>
                                <th style="width:220px">Base Path</th>
                                <th>Name (EN)</th>
                                <th>Name (TH)</th>
                                <th style="width:140px">Tax ID</th>
                                <th style="width:90px">Status</th>
                                <th>Detail</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr
                                v-for="r in infoResults"
                                :key="r.no"
                                :class="r.ok ? 'run-stepper-row-ok' : 'run-stepper-row-fail'"
                              >
                                <td class="tw-font-bold">{{ r.no }}</td>
                                <td class="run-stepper-base-path">{{ r.basePath }}</td>
                                <td>
                                  <span v-if="r.ok">{{ r.nameEN || '-' }}</span>
                                  <span v-else class="tw-text-gray-400">-</span>
                                </td>
                                <td>
                                  <span v-if="r.ok">{{ r.nameTH || '-' }}</span>
                                  <span v-else class="tw-text-gray-400">-</span>
                                </td>
                                <td class="tw-font-mono">
                                  <span v-if="r.ok">{{ r.taxID || '-' }}</span>
                                  <span v-else class="tw-text-gray-400">-</span>
                                </td>
                                <td>
                                  <v-chip
                                    small
                                    :color="r.ok ? 'green' : 'red'"
                                    text-color="white"
                                    class="tw-font-bold"
                                  >
                                    <v-icon left small>
                                      {{ r.ok ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                                    </v-icon>
                                    {{ r.ok ? 'OK' : 'FAIL' }}
                                  </v-chip>
                                </td>
                                <td class="tw-text-xs">
                                  <span v-if="!r.ok" class="tw-text-red-600">
                                    {{ r.code }}<span v-if="r.error">: {{ r.error }}</span>
                                  </span>
                                  <span v-else class="tw-text-gray-500">{{ r.latencyMs }} ms</span>
                                </td>
                              </tr>
                            </tbody>
                          </v-simple-table>
                        </div>
                      </div>

                      <div class="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-mt-5">
                        <v-btn
                          v-if="stepStatuses[2] === 'error'"
                          color="primary"
                          depressed
                          rounded
                          large
                          :disabled="isRunning"
                          @click="runGetCompanyInfo"
                        >
                          <v-icon left>mdi-refresh</v-icon>
                          Retry
                        </v-btn>
                        <v-btn
                          color="grey darken-2"
                          outlined
                          rounded
                          @click="cancelRun"
                        >
                          <v-icon left>mdi-close</v-icon>
                          Cancel
                        </v-btn>
                        <v-btn
                          v-if="stepStatuses[2] === 'success'"
                          color="primary"
                          depressed
                          rounded
                          large
                          @click="goToStep(3)"
                        >
                          Continue
                          <v-icon right>mdi-chevron-right</v-icon>
                        </v-btn>
                      </div>
                    </v-stepper-content>

                    <!-- ===== Step 3: Expense Mapping (STMAS × ISACC) ===== -->
                    <v-stepper-content :step="3">
                      <transition name="fade-slide" mode="out-in">
                        <div :key="stepStatuses[3]">
                          <!-- Running state -->
                          <div
                            v-if="stepStatuses[3] === 'running' && expenseProgressTotal > 0"
                            class="run-stepper-status running tw-mb-4"
                          >
                            <div class="tw-flex tw-items-center tw-gap-3 tw-mb-3">
                              <v-progress-circular
                                indeterminate
                                size="28"
                                width="3"
                                color="primary"
                              />
                              <div>
                                <div class="tw-font-bold tw-text-base">
                                  กำลังสร้างตาราง Expense Mapping (STMAS × ISACC)
                                </div>
                                <div class="tw-text-xs tw-text-gray-500">
                                  ประมวลผล {{ expenseProgressDone }} / {{ expenseProgressTotal }} base paths
                                </div>
                              </div>
                            </div>
                            <v-progress-linear
                              :value="(expenseProgressDone / expenseProgressTotal) * 100"
                              color="primary"
                              height="10"
                              rounded
                              striped
                            />
                          </div>

                          <!-- Error state -->
                          <div
                            v-else-if="expenseError || stepStatuses[3] === 'error'"
                            class="run-stepper-status error tw-mb-4"
                          >
                            <div class="tw-flex tw-items-center tw-gap-3">
                              <v-avatar size="44" color="red lighten-5">
                                <v-icon color="red" size="26">mdi-close-circle</v-icon>
                              </v-avatar>
                              <div>
                                <div class="tw-font-bold tw-text-base tw-text-red-600">
                                  สร้าง Expense Mapping ล้มเหลว
                                </div>
                                <div class="tw-text-sm tw-text-gray-700">
                                  <template v-if="expenseError">{{ expenseError }}</template>
                                  <template v-else>
                                    {{ expenseResults.length - expenseOkCount }} / {{ expenseResults.length }} base paths ล้มเหลว
                                  </template>
                                </div>
                              </div>
                            </div>
                          </div>

                          <!-- Success state -->
                          <div
                            v-else-if="stepStatuses[3] === 'success'"
                            class="run-stepper-status success tw-mb-4"
                          >
                            <div class="tw-flex tw-items-center tw-gap-3">
                              <v-avatar size="44" color="green lighten-5">
                                <v-icon color="green" size="26">mdi-check-decagram</v-icon>
                              </v-avatar>
                              <div>
                                <div class="tw-font-bold tw-text-base tw-text-green-700">
                                  Expense Mapping สำเร็จ
                                </div>
                                <div class="tw-text-sm tw-text-gray-700">
                                  รวม {{ expenseTotalItems }} รายการ · จับคู่ได้ {{ expenseTotalMatched }} รายการ
                                  ใน {{ expenseOkCount }} / {{ expenseResults.length }} base paths
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </transition>

                      <div
                        v-if="expenseResults.length"
                        class="run-stepper-results-panel"
                      >
                        <div class="run-stepper-results-toolbar">
                          <div>
                            <div class="run-stepper-results-title">Expense mapping summary</div>
                            <div class="run-stepper-results-subtitle">
                              {{ expenseOkCount }} ok, {{ expenseFailCount }} failed
                              · {{ expenseTotalMatched }} / {{ expenseTotalItems }} matched
                            </div>
                          </div>
                          <v-chip small label class="run-stepper-results-chip">
                            {{ expenseResults.length }} base paths
                          </v-chip>
                        </div>
                        <div class="run-stepper-table-wrap">
                          <v-simple-table dense>
                            <thead>
                              <tr class="run-stepper-table-head">
                                <th style="width:40px"></th>
                                <th style="width:60px">No.</th>
                                <th>Base Path</th>
                                <th style="width:90px">Status</th>
                                <th style="width:90px">Items</th>
                                <th style="width:120px">Matched</th>
                                <th style="width:90px">Latency</th>
                                <th>Detail</th>
                              </tr>
                            </thead>
                            <tbody>
                              <!-- eslint-disable vue/no-v-for-template-key-on-child -->
                              <template v-for="r in expenseResults">
                                <tr
                                  :key="'sum-' + r.no"
                                  :class="r.ok ? 'run-stepper-row-ok' : 'run-stepper-row-fail'"
                                >
                                  <td>
                                    <v-btn
                                      v-if="r.ok && r.items && r.items.length"
                                      icon
                                      x-small
                                      @click="toggleExpenseRow(r.no)"
                                    >
                                      <v-icon small>
                                        {{ expenseExpanded[r.no] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                                      </v-icon>
                                    </v-btn>
                                  </td>
                                  <td class="tw-font-bold">{{ r.no }}</td>
                                  <td class="run-stepper-base-path">{{ r.basePath }}</td>
                                  <td>
                                    <v-chip
                                      small
                                      :color="r.ok ? 'green' : 'red'"
                                      text-color="white"
                                      class="tw-font-bold"
                                    >
                                      <v-icon left small>
                                        {{ r.ok ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                                      </v-icon>
                                      {{ r.ok ? 'OK' : 'FAIL' }}
                                    </v-chip>
                                  </td>
                                  <td>
                                    <span v-if="r.ok" class="tw-font-bold">{{ r.totalCount }}</span>
                                    <span v-else>-</span>
                                  </td>
                                  <td>
                                    <span
                                      v-if="r.ok"
                                      :class="r.matchedCount === r.totalCount ? 'tw-text-green-700 tw-font-bold' : 'tw-text-orange-600 tw-font-bold'"
                                    >
                                      {{ r.matchedCount }} / {{ r.totalCount }}
                                    </span>
                                    <span v-else>-</span>
                                  </td>
                                  <td class="tw-text-xs">{{ r.latencyMs }} ms</td>
                                  <td class="tw-text-xs">
                                    <span v-if="!r.ok" class="tw-text-red-600">
                                      {{ r.code }}<span v-if="r.error">: {{ r.error }}</span>
                                    </span>
                                    <span v-else-if="r.unmatchedCount > 0" class="tw-text-orange-600">
                                      {{ r.unmatchedCount }} unmatched
                                    </span>
                                    <span v-else class="tw-text-gray-500">-</span>
                                  </td>
                                </tr>
                                <tr
                                  v-if="r.ok && expenseExpanded[r.no] && r.items && r.items.length"
                                  :key="'det-' + r.no"
                                  class="run-stepper-detail-row"
                                >
                                  <td colspan="8" class="tw-p-0">
                                    <div class="run-stepper-detail-wrap">
                                      <div class="run-stepper-detail-title">
                                        Items for base path #{{ r.no }} ({{ r.items.length }})
                                      </div>
                                      <v-simple-table dense>
                                        <thead>
                                          <tr class="run-stepper-table-head">
                                            <th>expCode</th>
                                            <th>expName</th>
                                            <th>acctCode</th>
                                            <th>acctName</th>
                                            <th style="width:80px">tranType</th>
                                            <th style="width:90px">Match</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          <tr
                                            v-for="(it, idx) in r.items"
                                            :key="r.no + ':' + idx"
                                            :class="it.matched ? '' : 'run-stepper-row-warn'"
                                          >
                                            <td class="tw-font-mono tw-text-xs">{{ it.expCode }}</td>
                                            <td class="tw-text-xs">{{ it.expName || '-' }}</td>
                                            <td class="tw-font-mono tw-text-xs">{{ it.acctCode || '-' }}</td>
                                            <td class="tw-text-xs">{{ it.acctName || '-' }}</td>
                                            <td class="tw-text-xs">{{ it.tranType }}</td>
                                            <td>
                                              <v-chip
                                                x-small
                                                :color="it.matched ? 'green lighten-4' : 'orange lighten-4'"
                                                :text-color="it.matched ? 'green darken-2' : 'orange darken-3'"
                                              >
                                                {{ it.matched ? 'matched' : 'no acct' }}
                                              </v-chip>
                                            </td>
                                          </tr>
                                        </tbody>
                                      </v-simple-table>
                                    </div>
                                  </td>
                                </tr>
                              </template>
                              <!-- eslint-enable vue/no-v-for-template-key-on-child -->
                            </tbody>
                          </v-simple-table>
                        </div>
                      </div>

                      <div class="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-mt-5">
                        <v-btn
                          v-if="stepStatuses[3] === 'error'"
                          color="primary"
                          depressed
                          rounded
                          large
                          :disabled="isRunning"
                          @click="runGetExpenseMapping"
                        >
                          <v-icon left>mdi-refresh</v-icon>
                          Retry
                        </v-btn>
                        <v-btn
                          color="grey darken-2"
                          outlined
                          rounded
                          @click="cancelRun"
                        >
                          <v-icon left>mdi-close</v-icon>
                          Cancel
                        </v-btn>
                        <v-btn
                          v-if="stepStatuses[3] === 'success'"
                          color="primary"
                          depressed
                          rounded
                          large
                          @click="goToStep(4)"
                        >
                          Continue
                          <v-icon right>mdi-chevron-right</v-icon>
                        </v-btn>
                      </div>
                    </v-stepper-content>

                    <!-- ===== Step 4: Payment Mapping (GLACC) ===== -->
                    <v-stepper-content :step="4">
                      <transition name="fade-slide" mode="out-in">
                        <div :key="stepStatuses[4]">
                          <!-- Running state -->
                          <div
                            v-if="stepStatuses[4] === 'running' && paymentProgressTotal > 0"
                            class="run-stepper-status running tw-mb-4"
                          >
                            <div class="tw-flex tw-items-center tw-gap-3 tw-mb-3">
                              <v-progress-circular
                                indeterminate
                                size="28"
                                width="3"
                                color="primary"
                              />
                              <div>
                                <div class="tw-font-bold tw-text-base">
                                  กำลังสร้างตาราง Payment Mapping (GLACC)
                                </div>
                                <div class="tw-text-xs tw-text-gray-500">
                                  ประมวลผล {{ paymentProgressDone }} / {{ paymentProgressTotal }} base paths
                                </div>
                              </div>
                            </div>
                            <v-progress-linear
                              :value="(paymentProgressDone / paymentProgressTotal) * 100"
                              color="primary"
                              height="10"
                              rounded
                              striped
                            />
                          </div>

                          <!-- Error state -->
                          <div
                            v-else-if="paymentError || stepStatuses[4] === 'error'"
                            class="run-stepper-status error tw-mb-4"
                          >
                            <div class="tw-flex tw-items-center tw-gap-3">
                              <v-avatar size="44" color="red lighten-5">
                                <v-icon color="red" size="26">mdi-close-circle</v-icon>
                              </v-avatar>
                              <div>
                                <div class="tw-font-bold tw-text-base tw-text-red-600">
                                  สร้าง Payment Mapping ล้มเหลว
                                </div>
                                <div class="tw-text-sm tw-text-gray-700">
                                  <template v-if="paymentError">{{ paymentError }}</template>
                                  <template v-else>
                                    {{ paymentResults.length - paymentOkCount }} / {{ paymentResults.length }} base paths ล้มเหลว
                                  </template>
                                </div>
                              </div>
                            </div>
                          </div>

                          <!-- Success state -->
                          <div
                            v-else-if="stepStatuses[4] === 'success'"
                            class="run-stepper-status success tw-mb-4"
                          >
                            <div class="tw-flex tw-items-center tw-gap-3">
                              <v-avatar size="44" color="green lighten-5">
                                <v-icon color="green" size="26">mdi-check-decagram</v-icon>
                              </v-avatar>
                              <div>
                                <div class="tw-font-bold tw-text-base tw-text-green-700">
                                  Payment Mapping สำเร็จ
                                </div>
                                <div class="tw-text-sm tw-text-gray-700">
                                  รวม {{ paymentTotalItems }} รายการบัญชีเงินสด/ธนาคาร
                                  ใน {{ paymentOkCount }} / {{ paymentResults.length }} base paths
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </transition>

                      <div
                        v-if="paymentResults.length"
                        class="run-stepper-results-panel"
                      >
                        <div class="run-stepper-results-toolbar">
                          <div>
                            <div class="run-stepper-results-title">Payment mapping summary</div>
                            <div class="run-stepper-results-subtitle">
                              {{ paymentOkCount }} ok, {{ paymentFailCount }} failed
                              · filter ACCNUM ขึ้นต้นด้วย "111" และ ACCTYP = "0"
                            </div>
                          </div>
                          <v-chip small label class="run-stepper-results-chip">
                            {{ paymentResults.length }} base paths
                          </v-chip>
                        </div>
                        <div class="run-stepper-table-wrap">
                          <v-simple-table dense>
                            <thead>
                              <tr class="run-stepper-table-head">
                                <th style="width:40px"></th>
                                <th style="width:60px">No.</th>
                                <th>Base Path</th>
                                <th style="width:90px">Status</th>
                                <th style="width:90px">Items</th>
                                <th style="width:120px">GLACC rows</th>
                                <th style="width:90px">Latency</th>
                                <th>Detail</th>
                              </tr>
                            </thead>
                            <tbody>
                              <!-- eslint-disable vue/no-v-for-template-key-on-child -->
                              <template v-for="r in paymentResults">
                                <tr
                                  :key="'sum-' + r.no"
                                  :class="r.ok ? (r.totalCount === 0 ? 'run-stepper-row-warn' : 'run-stepper-row-ok') : 'run-stepper-row-fail'"
                                >
                                  <td>
                                    <v-btn
                                      v-if="r.ok && r.items && r.items.length"
                                      icon
                                      x-small
                                      @click="togglePaymentRow(r.no)"
                                    >
                                      <v-icon small>
                                        {{ paymentExpanded[r.no] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                                      </v-icon>
                                    </v-btn>
                                  </td>
                                  <td class="tw-font-bold">{{ r.no }}</td>
                                  <td class="run-stepper-base-path">{{ r.basePath }}</td>
                                  <td>
                                    <v-chip
                                      small
                                      :color="r.ok ? 'green' : 'red'"
                                      text-color="white"
                                      class="tw-font-bold"
                                    >
                                      <v-icon left small>
                                        {{ r.ok ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                                      </v-icon>
                                      {{ r.ok ? 'OK' : 'FAIL' }}
                                    </v-chip>
                                  </td>
                                  <td>
                                    <span
                                      v-if="r.ok"
                                      :class="r.totalCount === 0 ? 'tw-text-orange-600 tw-font-bold' : 'tw-font-bold'"
                                    >
                                      {{ r.totalCount }}
                                    </span>
                                    <span v-else>-</span>
                                  </td>
                                  <td class="tw-text-xs tw-text-gray-600">
                                    <span v-if="r.ok">{{ r.glaccRowCount }}</span>
                                    <span v-else>-</span>
                                  </td>
                                  <td class="tw-text-xs">{{ r.latencyMs }} ms</td>
                                  <td class="tw-text-xs">
                                    <span v-if="!r.ok" class="tw-text-red-600">
                                      {{ r.code }}<span v-if="r.error">: {{ r.error }}</span>
                                    </span>
                                    <span v-else-if="r.totalCount === 0" class="tw-text-orange-600">
                                      ไม่พบบัญชี 111* / ACCTYP=0
                                    </span>
                                    <span v-else class="tw-text-gray-500">-</span>
                                  </td>
                                </tr>
                                <tr
                                  v-if="r.ok && paymentExpanded[r.no] && r.items && r.items.length"
                                  :key="'det-' + r.no"
                                  class="run-stepper-detail-row"
                                >
                                  <td colspan="8" class="tw-p-0">
                                    <div class="run-stepper-detail-wrap">
                                      <div class="run-stepper-detail-title">
                                        Items for base path #{{ r.no }} ({{ r.items.length }})
                                      </div>
                                      <v-simple-table dense>
                                        <thead>
                                          <tr class="run-stepper-table-head">
                                            <th>payCode</th>
                                            <th>payName</th>
                                            <th style="width:80px">tranType</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          <tr
                                            v-for="(it, idx) in r.items"
                                            :key="r.no + ':' + idx"
                                          >
                                            <td class="tw-font-mono tw-text-xs">{{ it.payCode }}</td>
                                            <td class="tw-text-xs">{{ it.payName || '-' }}</td>
                                            <td class="tw-text-xs">{{ it.tranType }}</td>
                                          </tr>
                                        </tbody>
                                      </v-simple-table>
                                    </div>
                                  </td>
                                </tr>
                              </template>
                              <!-- eslint-enable vue/no-v-for-template-key-on-child -->
                            </tbody>
                          </v-simple-table>
                        </div>
                      </div>

                      <div class="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-mt-5">
                        <v-btn
                          v-if="stepStatuses[4] === 'error'"
                          color="primary"
                          depressed
                          rounded
                          large
                          :disabled="isRunning"
                          @click="runGetPaymentMapping"
                        >
                          <v-icon left>mdi-refresh</v-icon>
                          Retry
                        </v-btn>
                        <v-btn
                          color="grey darken-2"
                          outlined
                          rounded
                          @click="cancelRun"
                        >
                          <v-icon left>mdi-close</v-icon>
                          Cancel
                        </v-btn>
                        <v-btn
                          v-if="stepStatuses[4] === 'success'"
                          color="primary"
                          depressed
                          rounded
                          large
                          @click="goToStep(5)"
                        >
                          Continue
                          <v-icon right>mdi-chevron-right</v-icon>
                        </v-btn>
                      </div>
                    </v-stepper-content>

                    <!-- ===== Step 5: Copy Master Data (Vat / WHT / Expense / PaymentAccount) ===== -->
                    <v-stepper-content :step="5">
                      <transition name="fade-slide" mode="out-in">
                        <div :key="stepStatuses[5]">
                          <!-- Running state -->
                          <div
                            v-if="stepStatuses[5] === 'running' && masterLoading"
                            class="run-stepper-status running tw-mb-4"
                          >
                            <div class="tw-flex tw-items-center tw-gap-3">
                              <v-progress-circular
                                indeterminate
                                size="28"
                                width="3"
                                color="primary"
                              />
                              <div>
                                <div class="tw-font-bold tw-text-base">
                                  กำลังอ่านข้อมูล Master จากบริษัทต้นทาง
                                </div>
                                <div class="tw-text-xs tw-text-gray-500">
                                  /Companies/{{ form.companyId }}/PurchaseInvoiceMaster/*
                                </div>
                              </div>
                            </div>
                          </div>

                          <!-- Error state -->
                          <div
                            v-else-if="masterError || stepStatuses[5] === 'error'"
                            class="run-stepper-status error tw-mb-4"
                          >
                            <div class="tw-flex tw-items-center tw-gap-3">
                              <v-avatar size="44" color="red lighten-5">
                                <v-icon color="red" size="26">mdi-close-circle</v-icon>
                              </v-avatar>
                              <div>
                                <div class="tw-font-bold tw-text-base tw-text-red-600">
                                  อ่าน Master Data ไม่สำเร็จ
                                </div>
                                <div class="tw-text-sm tw-text-gray-700">
                                  {{ masterError || "Unknown error" }}
                                </div>
                              </div>
                            </div>
                          </div>

                          <!-- Success state -->
                          <div
                            v-else-if="stepStatuses[5] === 'success'"
                            class="run-stepper-status success tw-mb-4"
                          >
                            <div class="tw-flex tw-items-center tw-gap-3">
                              <v-avatar size="44" color="green lighten-5">
                                <v-icon color="green" size="26">mdi-check-decagram</v-icon>
                              </v-avatar>
                              <div>
                                <div class="tw-font-bold tw-text-base tw-text-green-700">
                                  อ่าน Master Data สำเร็จ
                                </div>
                                <div class="tw-text-sm tw-text-gray-700">
                                  พร้อมคัดลอก Vat / WHT และใช้โครงสร้าง Expense / PaymentAccount
                                  สำหรับ {{ copyNumbers.length }} สำเนาบริษัท
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </transition>

                      <div
                        v-if="masterAllLoaded"
                        class="run-stepper-results-panel"
                      >
                        <div class="run-stepper-results-toolbar">
                          <div>
                            <div class="run-stepper-results-title">Reference master docs</div>
                            <div class="run-stepper-results-subtitle">
                              Source: /Companies/{{ form.companyId }}/PurchaseInvoiceMaster
                            </div>
                          </div>
                          <v-chip small label class="run-stepper-results-chip">
                            {{ masterDocSummary.length }} docs
                          </v-chip>
                        </div>
                        <div class="run-stepper-table-wrap">
                          <v-simple-table dense>
                            <thead>
                              <tr class="run-stepper-table-head">
                                <th>Doc</th>
                                <th style="width:160px">Array Field</th>
                                <th style="width:120px">Items</th>
                                <th>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="d in masterDocSummary" :key="d.key">
                                <td class="tw-font-bold">{{ d.label }}</td>
                                <td class="tw-font-mono tw-text-xs">
                                  <span v-if="d.arrayField">{{ d.arrayField }}</span>
                                  <span v-else class="tw-text-gray-400">-</span>
                                </td>
                                <td class="tw-font-bold">{{ d.count }}</td>
                                <td class="tw-text-xs tw-text-gray-600">
                                  <span v-if="d.key === 'company'">
                                    Clone ทุก field · override <strong>nameEN / nameTH / taxID</strong> ต่อ base path
                                  </span>
                                  <span v-else-if="d.key === 'vat' || d.key === 'withholdingTax'">
                                    คัดลอกตามต้นฉบับไปยังทุกสำเนา
                                  </span>
                                  <span v-else-if="d.key === 'expense'">
                                    ใช้โครงสร้าง · ใส่ items จาก Expense Mapping (ขั้น 3) ต่อ base path
                                  </span>
                                  <span v-else-if="d.key === 'paymentAccount'">
                                    ใช้โครงสร้าง · ใส่ items จาก Payment Mapping (ขั้น 4) ต่อ base path
                                  </span>
                                </td>
                              </tr>
                            </tbody>
                          </v-simple-table>
                        </div>
                      </div>

                      <div
                        v-if="masterMissing.length"
                        class="tw-text-xs tw-text-red-600 tw-mt-3"
                      >
                        Missing master docs: <strong>{{ masterMissing.join(", ") }}</strong> —
                        please verify the reference company has them populated.
                      </div>

                      <div class="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-mt-5">
                        <v-btn
                          v-if="stepStatuses[5] === 'error'"
                          color="primary"
                          depressed
                          rounded
                          large
                          :disabled="isRunning"
                          @click="runCopyMasterData"
                        >
                          <v-icon left>mdi-refresh</v-icon>
                          Retry
                        </v-btn>
                        <v-btn
                          color="grey darken-2"
                          outlined
                          rounded
                          @click="cancelRun"
                        >
                          <v-icon left>mdi-close</v-icon>
                          Cancel
                        </v-btn>
                        <v-btn
                          v-if="stepStatuses[5] === 'success'"
                          color="primary"
                          depressed
                          rounded
                          large
                          @click="goToStep(6)"
                        >
                          Continue
                          <v-icon right>mdi-chevron-right</v-icon>
                        </v-btn>
                      </div>
                    </v-stepper-content>

                    <!-- ===== Step 6: Sync Vendors (APMAS) ===== -->
                    <v-stepper-content :step="6">
                      <transition name="fade-slide" mode="out-in">
                        <div :key="stepStatuses[6]">
                          <!-- Running state -->
                          <div
                            v-if="stepStatuses[6] === 'running' && vendorProgressTotal > 0"
                            class="run-stepper-status running tw-mb-4"
                          >
                            <div class="tw-flex tw-items-center tw-gap-3 tw-mb-3">
                              <v-progress-circular
                                indeterminate
                                size="28"
                                width="3"
                                color="primary"
                              />
                              <div>
                                <div class="tw-font-bold tw-text-base">
                                  กำลังอ่านข้อมูล Vendor (APMAS) ต่อสำเนาบริษัท
                                </div>
                                <div class="tw-text-xs tw-text-gray-500">
                                  ประมวลผล {{ vendorProgressDone }} / {{ vendorProgressTotal }} base paths
                                </div>
                              </div>
                            </div>
                            <v-progress-linear
                              :value="(vendorProgressDone / vendorProgressTotal) * 100"
                              color="primary"
                              height="10"
                              rounded
                              striped
                            />
                          </div>

                          <!-- Error state -->
                          <div
                            v-else-if="vendorError || stepStatuses[6] === 'error'"
                            class="run-stepper-status error tw-mb-4"
                          >
                            <div class="tw-flex tw-items-center tw-gap-3">
                              <v-avatar size="44" color="red lighten-5">
                                <v-icon color="red" size="26">mdi-close-circle</v-icon>
                              </v-avatar>
                              <div>
                                <div class="tw-font-bold tw-text-base tw-text-red-600">
                                  อ่าน Vendor ล้มเหลว
                                </div>
                                <div class="tw-text-sm tw-text-gray-700">
                                  <template v-if="vendorError">{{ vendorError }}</template>
                                  <template v-else>
                                    {{ vendorResults.length - vendorOkCount }} / {{ vendorResults.length }} base paths ล้มเหลว
                                  </template>
                                </div>
                              </div>
                            </div>
                          </div>

                          <!-- Success state -->
                          <div
                            v-else-if="stepStatuses[6] === 'success'"
                            class="run-stepper-status success tw-mb-4"
                          >
                            <div class="tw-flex tw-items-center tw-gap-3">
                              <v-avatar size="44" color="green lighten-5">
                                <v-icon color="green" size="26">mdi-check-decagram</v-icon>
                              </v-avatar>
                              <div>
                                <div class="tw-font-bold tw-text-base tw-text-green-700">
                                  อ่าน Vendor สำเร็จ
                                </div>
                                <div class="tw-text-sm tw-text-gray-700">
                                  รวม {{ vendorTotalItems }} vendors
                                  <span v-if="vendorTotalSkipped > 0">
                                    · ข้าม {{ vendorTotalSkipped }} (รหัสซ้ำ/ว่าง)
                                  </span>
                                  ใน {{ vendorOkCount }} / {{ vendorResults.length }} base paths
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </transition>

                      <div
                        v-if="vendorResults.length"
                        class="run-stepper-results-panel"
                      >
                        <div class="run-stepper-results-toolbar">
                          <div>
                            <div class="run-stepper-results-title">Vendor sync summary</div>
                            <div class="run-stepper-results-subtitle">
                              {{ vendorOkCount }} ok, {{ vendorFailCount }} failed
                              · APMAS → PurchaseInvoiceVendors
                            </div>
                          </div>
                          <v-chip small label class="run-stepper-results-chip">
                            {{ vendorResults.length }} base paths
                          </v-chip>
                        </div>
                        <div class="run-stepper-table-wrap">
                          <v-simple-table dense>
                            <thead>
                              <tr class="run-stepper-table-head">
                                <th style="width:40px"></th>
                                <th style="width:60px">No.</th>
                                <th>Base Path</th>
                                <th style="width:90px">Status</th>
                                <th style="width:90px">Vendors</th>
                                <th style="width:100px">Skipped</th>
                                <th style="width:120px">APMAS rows</th>
                                <th style="width:90px">Latency</th>
                                <th>Detail</th>
                              </tr>
                            </thead>
                            <tbody>
                              <!-- eslint-disable vue/no-v-for-template-key-on-child -->
                              <template v-for="r in vendorResults">
                                <tr
                                  :key="'sum-' + r.no"
                                  :class="r.ok ? (r.totalCount === 0 ? 'run-stepper-row-warn' : 'run-stepper-row-ok') : 'run-stepper-row-fail'"
                                >
                                  <td>
                                    <v-btn
                                      v-if="r.ok && r.items && r.items.length"
                                      icon
                                      x-small
                                      @click="toggleVendorRow(r.no)"
                                    >
                                      <v-icon small>
                                        {{ vendorExpanded[r.no] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                                      </v-icon>
                                    </v-btn>
                                  </td>
                                  <td class="tw-font-bold">{{ r.no }}</td>
                                  <td class="run-stepper-base-path">{{ r.basePath }}</td>
                                  <td>
                                    <v-chip
                                      small
                                      :color="r.ok ? 'green' : 'red'"
                                      text-color="white"
                                      class="tw-font-bold"
                                    >
                                      <v-icon left small>
                                        {{ r.ok ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                                      </v-icon>
                                      {{ r.ok ? 'OK' : 'FAIL' }}
                                    </v-chip>
                                  </td>
                                  <td>
                                    <span v-if="r.ok" class="tw-font-bold">{{ r.totalCount }}</span>
                                    <span v-else>-</span>
                                  </td>
                                  <td>
                                    <span
                                      v-if="r.ok"
                                      :class="r.skippedCount > 0 ? 'tw-text-orange-600 tw-font-bold' : 'tw-text-gray-500'"
                                    >
                                      {{ r.skippedCount }}
                                    </span>
                                    <span v-else>-</span>
                                  </td>
                                  <td class="tw-text-xs tw-text-gray-600">
                                    <span v-if="r.ok">{{ r.apmasRowCount }}</span>
                                    <span v-else>-</span>
                                  </td>
                                  <td class="tw-text-xs">{{ r.latencyMs }} ms</td>
                                  <td class="tw-text-xs">
                                    <span v-if="!r.ok" class="tw-text-red-600">
                                      {{ r.code }}<span v-if="r.error">: {{ r.error }}</span>
                                    </span>
                                    <span v-else-if="r.totalCount === 0" class="tw-text-orange-600">
                                      ไม่มีรายการ vendor
                                    </span>
                                    <span v-else class="tw-text-gray-500">-</span>
                                  </td>
                                </tr>
                                <tr
                                  v-if="r.ok && vendorExpanded[r.no] && r.items && r.items.length"
                                  :key="'det-' + r.no"
                                  class="run-stepper-detail-row"
                                >
                                  <td colspan="9" class="tw-p-0">
                                    <div class="run-stepper-detail-wrap">
                                      <div class="run-stepper-detail-title">
                                        Vendors for base path #{{ r.no }} ({{ r.items.length }})
                                        <span v-if="r.items.length > 50" class="tw-font-normal tw-text-gray-500 tw-normal-case">
                                          — แสดง 50 รายการแรก
                                        </span>
                                      </div>
                                      <v-simple-table dense>
                                        <thead>
                                          <tr class="run-stepper-table-head">
                                            <th style="width:120px">venCode</th>
                                            <th style="width:80px">Prefix</th>
                                            <th>Name</th>
                                            <th style="width:140px">Tax ID</th>
                                            <th style="width:80px">Branch</th>
                                            <th>Address</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          <tr
                                            v-for="(it, idx) in r.items.slice(0, 50)"
                                            :key="r.no + ':' + idx"
                                          >
                                            <td class="tw-font-mono tw-text-xs">{{ it.venCode }}</td>
                                            <td class="tw-text-xs">{{ it.venPrefix || '-' }}</td>
                                            <td class="tw-text-xs">{{ it.venName || '-' }}</td>
                                            <td class="tw-font-mono tw-text-xs">{{ it.venTaxId || '-' }}</td>
                                            <td class="tw-font-mono tw-text-xs">{{ it.venBranch }}</td>
                                            <td class="tw-text-xs tw-text-gray-600">{{ it.venAddr || '-' }}</td>
                                          </tr>
                                        </tbody>
                                      </v-simple-table>
                                    </div>
                                  </td>
                                </tr>
                              </template>
                              <!-- eslint-enable vue/no-v-for-template-key-on-child -->
                            </tbody>
                          </v-simple-table>
                        </div>
                      </div>

                      <div class="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-mt-5">
                        <v-btn
                          v-if="stepStatuses[6] === 'error'"
                          color="primary"
                          depressed
                          rounded
                          large
                          :disabled="isRunning"
                          @click="runSyncVendors"
                        >
                          <v-icon left>mdi-refresh</v-icon>
                          Retry
                        </v-btn>
                        <v-btn
                          color="grey darken-2"
                          outlined
                          rounded
                          @click="cancelRun"
                        >
                          <v-icon left>mdi-close</v-icon>
                          Cancel
                        </v-btn>
                        <v-btn
                          v-if="stepStatuses[6] === 'success'"
                          color="primary"
                          depressed
                          rounded
                          large
                          @click="goToStep(7)"
                        >
                          Continue
                          <v-icon right>mdi-chevron-right</v-icon>
                        </v-btn>
                      </div>
                    </v-stepper-content>

                    <!-- ===== Step 7: Verify Structure ===== -->
                    <v-stepper-content :step="7">
                      <transition name="fade-slide" mode="out-in">
                        <div :key="stepStatuses[7]">
                          <!-- Error state -->
                          <div
                            v-if="stepStatuses[7] === 'error'"
                            class="run-stepper-status error tw-mb-4"
                          >
                            <div class="tw-flex tw-items-center tw-gap-3">
                              <v-avatar size="44" color="red lighten-5">
                                <v-icon color="red" size="26">mdi-close-circle</v-icon>
                              </v-avatar>
                              <div>
                                <div class="tw-font-bold tw-text-base tw-text-red-600">
                                  พบปัญหาที่ต้องแก้ไขก่อน apply
                                </div>
                                <div class="tw-text-sm tw-text-gray-700">
                                  {{ verifyOkRows }} / {{ verifyTotalRows }} base paths พร้อม
                                </div>
                              </div>
                            </div>
                          </div>

                          <!-- Success state -->
                          <div
                            v-else-if="stepStatuses[7] === 'success'"
                            class="run-stepper-status success tw-mb-4"
                          >
                            <div class="tw-flex tw-items-center tw-gap-3">
                              <v-avatar size="44" color="green lighten-5">
                                <v-icon color="green" size="26">mdi-shield-check</v-icon>
                              </v-avatar>
                              <div>
                                <div class="tw-font-bold tw-text-base tw-text-green-700">
                                  ตรวจสอบผ่าน · พร้อม apply
                                </div>
                                <div class="tw-text-sm tw-text-gray-700">
                                  {{ verifyOkRows }} / {{ verifyTotalRows }} base paths พร้อมสร้างสำเนา
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </transition>

                      <!-- Plan summary -->
                      <div class="run-stepper-results-panel tw-mb-4">
                        <div class="run-stepper-results-toolbar">
                          <div>
                            <div class="run-stepper-results-title">Plan summary</div>
                            <div class="run-stepper-results-subtitle">
                              สรุปข้อมูลทั้งหมดก่อนเริ่ม apply
                            </div>
                          </div>
                          <v-chip small label class="run-stepper-results-chip">
                            {{ copyNumbers.length }} copies
                          </v-chip>
                        </div>
                        <div class="run-stepper-detail-wrap tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3 tw-text-xs">
                          <div>
                            <div class="run-stepper-detail-title">Source</div>
                            <div><strong>Company:</strong> {{ form.companyId }}</div>
                            <div><strong>Range:</strong> {{ form.copyFrom }} → {{ form.copyTo }} ({{ copyNumbers.length }} copies)</div>
                            <div><strong>Pattern:</strong> <span class="tw-font-mono">{{ form.namePattern }}</span></div>
                          </div>
                          <div>
                            <div class="run-stepper-detail-title">FoxPro gateway</div>
                            <div><strong>API:</strong> <span class="tw-font-mono">{{ foxpro.apiUrl }}</span></div>
                            <div><strong>SMB:</strong> <span class="tw-font-mono">{{ foxpro.networkPath }}/{{ foxpro.shareName }}</span></div>
                            <div><strong>User:</strong> <span class="tw-font-mono">{{ foxpro.domain ? foxpro.domain + '\\' : '' }}{{ foxpro.username }}</span></div>
                          </div>
                          <div>
                            <div class="run-stepper-detail-title">Master data</div>
                            <div v-for="d in masterDocSummary" :key="d.key">
                              <strong>{{ d.label }}:</strong>
                              <span v-if="d.doc">
                                {{ d.count }} items
                                <span v-if="d.arrayField" class="tw-text-gray-500">
                                  ({{ d.arrayField }})
                                </span>
                              </span>
                              <span v-else class="tw-text-red-600">missing</span>
                            </div>
                          </div>
                          <div>
                            <div class="run-stepper-detail-title">
                              Recipients (login access)
                              <span v-if="verifyEmailsLoading" class="tw-text-gray-500 tw-font-normal">
                                — กำลัง resolve uid…
                              </span>
                            </div>
                            <div v-if="verifyEmails.length">
                              <div
                                v-for="e in verifyEmails"
                                :key="e.email"
                                class="tw-flex tw-items-center tw-gap-2 tw-mb-1"
                              >
                                <v-icon
                                  small
                                  :color="e.status === 'ok' ? 'green' : (e.status === 'pending' ? 'grey' : 'red')"
                                >
                                  {{
                                    e.status === 'ok'
                                      ? 'mdi-check-circle'
                                      : (e.status === 'pending'
                                        ? 'mdi-clock-outline'
                                        : 'mdi-close-circle')
                                  }}
                                </v-icon>
                                <span class="tw-font-mono tw-text-xs">{{ e.email }}</span>
                                <v-chip
                                  v-if="form.emailRoles[e.email]"
                                  x-small
                                  :color="form.emailRoles[e.email] === 'developer' ? 'purple lighten-4' : (form.emailRoles[e.email] === 'user' ? 'blue lighten-4' : 'green lighten-4')"
                                  :text-color="form.emailRoles[e.email] === 'developer' ? 'purple darken-2' : (form.emailRoles[e.email] === 'user' ? 'blue darken-2' : 'green darken-2')"
                                  class="tw-font-bold"
                                >
                                  {{ form.emailRoles[e.email] }}
                                </v-chip>
                                <span
                                  v-if="e.status === 'ok'"
                                  class="tw-text-xs tw-text-gray-600"
                                >
                                  · {{ e.name || "(no name)" }}
                                  <span class="tw-text-gray-400 tw-font-mono">{{ e.uid.slice(0, 8) }}…</span>
                                </span>
                                <span
                                  v-else-if="e.status === 'not-found'"
                                  class="tw-text-xs tw-text-red-600 tw-font-bold"
                                >
                                  · ไม่พบใน Users
                                </span>
                                <span
                                  v-else-if="e.status === 'error'"
                                  class="tw-text-xs tw-text-red-600"
                                >
                                  · {{ e.error || "lookup failed" }}
                                </span>
                              </div>
                            </div>
                            <div v-else-if="form.emails && form.emails.length" class="tw-text-gray-500">
                              {{ form.emails.length }} email — รอ Verify resolve
                            </div>
                            <div v-else class="tw-text-red-600 tw-font-bold">
                              ไม่ได้ระบุ — สำเนาจะไม่มีผู้ใช้เข้าถึงได้
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Clone strategy -->
                      <div class="run-stepper-results-panel tw-mb-4">
                        <div class="run-stepper-results-toolbar">
                          <div>
                            <div class="run-stepper-results-title">Clone strategy</div>
                            <div class="run-stepper-results-subtitle">
                              ทุกสำเนาใช้ข้อมูลเดียวกับบริษัทต้นทาง · มีเฉพาะฟิลด์ด้านล่างที่ override ต่อ base path
                            </div>
                          </div>
                          <v-chip small label class="run-stepper-results-chip">
                            ref · {{ form.companyId }}
                          </v-chip>
                        </div>
                        <div class="run-stepper-table-wrap">
                          <v-simple-table dense>
                            <thead>
                              <tr class="run-stepper-table-head">
                                <th style="width:240px">Path</th>
                                <th style="width:140px">Action</th>
                                <th>Source</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td class="tw-font-mono tw-text-xs">Companies/{newId}</td>
                                <td>
                                  <v-chip x-small color="orange lighten-4" text-color="orange darken-3">override</v-chip>
                                </td>
                                <td class="tw-text-xs">
                                  Clone ref · override <strong>nameEN / nameTH / taxID</strong> ต่อ base path (จากขั้น 2)
                                </td>
                              </tr>
                              <tr>
                                <td class="tw-font-mono tw-text-xs">PurchaseInvoiceMaster/Vat</td>
                                <td>
                                  <v-chip x-small color="green lighten-4" text-color="green darken-2">copy</v-chip>
                                </td>
                                <td class="tw-text-xs">1:1 จาก ref</td>
                              </tr>
                              <tr>
                                <td class="tw-font-mono tw-text-xs">PurchaseInvoiceMaster/WithholdingTax</td>
                                <td>
                                  <v-chip x-small color="green lighten-4" text-color="green darken-2">copy</v-chip>
                                </td>
                                <td class="tw-text-xs">1:1 จาก ref</td>
                              </tr>
                              <tr>
                                <td class="tw-font-mono tw-text-xs">PurchaseInvoiceMaster/Expense</td>
                                <td>
                                  <v-chip x-small color="orange lighten-4" text-color="orange darken-3">override</v-chip>
                                </td>
                                <td class="tw-text-xs">โครงสร้างจาก ref · items จาก Expense Mapping (ขั้น 3) ต่อ base path</td>
                              </tr>
                              <tr>
                                <td class="tw-font-mono tw-text-xs">PurchaseInvoiceMaster/PaymentAccount</td>
                                <td>
                                  <v-chip x-small color="orange lighten-4" text-color="orange darken-3">override</v-chip>
                                </td>
                                <td class="tw-text-xs">โครงสร้างจาก ref · items จาก Payment Mapping (ขั้น 4) ต่อ base path</td>
                              </tr>
                              <tr>
                                <td class="tw-font-mono tw-text-xs">PurchaseInvoiceVendors/*</td>
                                <td>
                                  <v-chip x-small color="orange lighten-4" text-color="orange darken-3">override</v-chip>
                                </td>
                                <td class="tw-text-xs">สร้างใหม่จาก APMAS ต่อ base path (ขั้น 6) — ไม่คัดลอกจาก ref</td>
                              </tr>
                              <tr>
                                <td class="tw-font-mono tw-text-xs">FoxproConfig/config</td>
                                <td>
                                  <v-chip x-small color="orange lighten-4" text-color="orange darken-3">override</v-chip>
                                </td>
                                <td class="tw-text-xs">Clone ref · override <strong>basePath</strong> ต่อ base path</td>
                              </tr>
                              <tr>
                                <td class="tw-font-mono tw-text-xs">(other subcollections)</td>
                                <td>
                                  <v-chip x-small color="green lighten-4" text-color="green darken-2">copy</v-chip>
                                </td>
                                <td class="tw-text-xs">1:1 จาก ref · จะระบุรายการจริงในขั้น apply</td>
                              </tr>
                            </tbody>
                          </v-simple-table>
                        </div>
                      </div>

                      <!-- Issues / warnings -->
                      <div v-if="verifyIssues.length" class="tw-mb-4 tw-flex tw-flex-col tw-gap-2">
                        <div
                          v-for="(iss, idx) in verifyIssues"
                          :key="idx"
                          class="run-stepper-status"
                          :class="iss.severity === 'error' ? 'error' : (iss.severity === 'warn' ? 'running' : 'info')"
                        >
                          <div class="tw-flex tw-items-center tw-gap-3">
                            <v-icon
                              :color="iss.severity === 'error' ? 'red' : (iss.severity === 'warn' ? 'orange' : 'primary')"
                              size="22"
                            >
                              {{ iss.severity === 'error' ? 'mdi-alert-circle' : (iss.severity === 'warn' ? 'mdi-alert' : 'mdi-information') }}
                            </v-icon>
                            <div class="tw-text-sm">{{ iss.message }}</div>
                          </div>
                        </div>
                      </div>

                      <!-- Per-copy verify table -->
                      <div class="run-stepper-results-panel">
                        <div class="run-stepper-results-toolbar">
                          <div>
                            <div class="run-stepper-results-title">Per-copy verification</div>
                            <div class="run-stepper-results-subtitle">
                              {{ verifyOkRows }} ready, {{ verifyTotalRows - verifyOkRows }} with issues
                            </div>
                          </div>
                          <v-chip small label class="run-stepper-results-chip">
                            {{ verifyTotalRows }} rows
                          </v-chip>
                        </div>
                        <div class="run-stepper-table-wrap">
                          <v-simple-table dense>
                            <thead>
                              <tr class="run-stepper-table-head">
                                <th style="width:60px">No.</th>
                                <th style="width:200px">Company name</th>
                                <th>Name (TH)</th>
                                <th style="width:140px">Tax ID</th>
                                <th style="width:90px">Expense</th>
                                <th style="width:90px">Payment</th>
                                <th style="width:90px">Vendor</th>
                                <th style="width:80px">Status</th>
                                <th>Issues</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr
                                v-for="r in verifyRows"
                                :key="r.no"
                                :class="r.ok ? (r.issues.length ? 'run-stepper-row-warn' : 'run-stepper-row-ok') : 'run-stepper-row-fail'"
                              >
                                <td class="tw-font-bold">{{ r.no }}</td>
                                <td class="tw-text-xs">{{ r.companyName || '-' }}</td>
                                <td class="tw-text-xs">{{ r.nameTH || r.nameEN || '-' }}</td>
                                <td class="tw-font-mono tw-text-xs">{{ r.taxID || '-' }}</td>
                                <td>
                                  <span v-if="r.expenseCount !== null">
                                    <strong>{{ r.expenseMatched }}</strong>
                                    <span class="tw-text-gray-500">/ {{ r.expenseCount }}</span>
                                  </span>
                                  <span v-else class="tw-text-red-600">-</span>
                                </td>
                                <td>
                                  <span v-if="r.paymentCount !== null" class="tw-font-bold">
                                    {{ r.paymentCount }}
                                  </span>
                                  <span v-else class="tw-text-red-600">-</span>
                                </td>
                                <td>
                                  <span v-if="r.vendorCount !== null" class="tw-font-bold">
                                    {{ r.vendorCount }}
                                  </span>
                                  <span v-else class="tw-text-red-600">-</span>
                                </td>
                                <td>
                                  <v-chip
                                    x-small
                                    :color="r.ok ? (r.issues.length ? 'orange' : 'green') : 'red'"
                                    text-color="white"
                                    class="tw-font-bold"
                                  >
                                    {{ r.ok ? (r.issues.length ? 'WARN' : 'OK') : 'FAIL' }}
                                  </v-chip>
                                </td>
                                <td class="tw-text-xs">
                                  <span v-if="r.issues.length" class="tw-text-orange-700">
                                    {{ r.issues.join(', ') }}
                                  </span>
                                  <span v-else class="tw-text-gray-400">-</span>
                                </td>
                              </tr>
                            </tbody>
                          </v-simple-table>
                        </div>
                      </div>

                      <div class="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-mt-5">
                        <v-btn
                          v-if="stepStatuses[7] === 'error'"
                          color="primary"
                          depressed
                          rounded
                          large
                          :disabled="isRunning"
                          @click="runVerifyStructure"
                        >
                          <v-icon left>mdi-refresh</v-icon>
                          Re-check
                        </v-btn>
                        <v-btn
                          color="grey darken-2"
                          outlined
                          rounded
                          @click="cancelRun"
                        >
                          <v-icon left>mdi-close</v-icon>
                          Cancel
                        </v-btn>
                        <v-btn
                          v-if="stepStatuses[7] === 'success'"
                          color="primary"
                          depressed
                          rounded
                          large
                          @click="goToStep(8)"
                        >
                          Confirm &amp; Continue
                          <v-icon right>mdi-chevron-right</v-icon>
                        </v-btn>
                      </div>
                    </v-stepper-content>

                    <!-- ===== Step 8: Apply (create duplicate companies) ===== -->
                    <v-stepper-content :step="8">
                      <transition name="fade-slide" mode="out-in">
                        <div :key="stepStatuses[8]">
                          <!-- Logo upload state -->
                          <div
                            v-if="stepStatuses[8] === 'running' && applyLogoUploading"
                            class="run-stepper-status running tw-mb-4"
                          >
                            <div class="tw-flex tw-items-center tw-gap-3">
                              <v-progress-circular
                                indeterminate
                                size="28"
                                width="3"
                                color="primary"
                              />
                              <div>
                                <div class="tw-font-bold tw-text-base">กำลังอัปโหลดโลโก้</div>
                                <div class="tw-text-xs tw-text-gray-500">
                                  ต้องเสร็จก่อนเริ่มสร้างสำเนา
                                </div>
                              </div>
                            </div>
                          </div>

                          <!-- Running state -->
                          <div
                            v-else-if="stepStatuses[8] === 'running' && applyProgressTotal > 0"
                            class="run-stepper-status running tw-mb-4"
                          >
                            <div class="tw-flex tw-items-center tw-gap-3 tw-mb-3">
                              <v-progress-circular
                                indeterminate
                                size="28"
                                width="3"
                                color="primary"
                              />
                              <div>
                                <div class="tw-font-bold tw-text-base">
                                  กำลังสร้างสำเนาบริษัท
                                </div>
                                <div class="tw-text-xs tw-text-gray-500">
                                  สร้าง {{ applyProgressDone }} / {{ applyProgressTotal }} copies
                                  · เวลา {{ applyElapsedSec }}s
                                  <span v-if="applyRunId" class="tw-font-mono tw-ml-2">
                                    run: {{ applyRunId.slice(0, 8) }}…
                                  </span>
                                </div>
                              </div>
                            </div>
                            <v-progress-linear
                              :value="(applyProgressDone / applyProgressTotal) * 100"
                              color="primary"
                              height="10"
                              rounded
                              striped
                            />
                          </div>

                          <!-- Error state -->
                          <div
                            v-else-if="applyError || stepStatuses[8] === 'error'"
                            class="run-stepper-status error tw-mb-4"
                          >
                            <div class="tw-flex tw-items-center tw-gap-3">
                              <v-avatar size="44" color="red lighten-5">
                                <v-icon color="red" size="26">mdi-close-circle</v-icon>
                              </v-avatar>
                              <div>
                                <div class="tw-font-bold tw-text-base tw-text-red-600">
                                  สร้างสำเนาบริษัทไม่สำเร็จทั้งหมด
                                </div>
                                <div class="tw-text-sm tw-text-gray-700">
                                  <template v-if="applyError">{{ applyError }}</template>
                                  <template v-else>
                                    สำเร็จ {{ applyOkCount }} · ล้มเหลว {{ applyFailCount }} จาก {{ applyResults.length }} copies
                                    — กด Retry เพื่อทำต่อจาก runId เดิม
                                  </template>
                                </div>
                              </div>
                            </div>
                          </div>

                          <!-- Success state -->
                          <div
                            v-else-if="stepStatuses[8] === 'success'"
                            class="run-stepper-status success tw-mb-4"
                          >
                            <div class="tw-flex tw-items-center tw-gap-3">
                              <v-avatar size="44" color="green lighten-5">
                                <v-icon color="green" size="26">mdi-check-decagram</v-icon>
                              </v-avatar>
                              <div>
                                <div class="tw-font-bold tw-text-base tw-text-green-700">
                                  สร้างสำเนาบริษัทสำเร็จทั้งหมด
                                </div>
                                <div class="tw-text-sm tw-text-gray-700">
                                  สร้าง {{ applyOkCount }} / {{ applyResults.length }} copies
                                  · เวลารวม {{ applyElapsedSec }}s
                                  <span v-if="applyRunId" class="tw-font-mono tw-ml-2">
                                    run: {{ applyRunId.slice(0, 8) }}…
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </transition>

                      <!-- Per-copy results table -->
                      <div
                        v-if="applyResults.length"
                        class="run-stepper-results-panel"
                      >
                        <div class="run-stepper-results-toolbar">
                          <div>
                            <div class="run-stepper-results-title">Apply results</div>
                            <div class="run-stepper-results-subtitle">
                              {{ applyOkCount }} created, {{ applyFailCount }} failed
                            </div>
                          </div>
                          <v-chip small label class="run-stepper-results-chip">
                            {{ applyResults.length }} / {{ applyProgressTotal }}
                          </v-chip>
                        </div>
                        <div class="run-stepper-table-wrap">
                          <v-simple-table dense>
                            <thead>
                              <tr class="run-stepper-table-head">
                                <th style="width:60px">No.</th>
                                <th style="width:90px">Status</th>
                                <th>New Company ID</th>
                                <th style="width:100px">Docs</th>
                                <th style="width:100px">Vendors</th>
                                <th>Detail</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr
                                v-for="r in applyResults"
                                :key="r.no"
                                :class="r.ok ? 'run-stepper-row-ok' : 'run-stepper-row-fail'"
                              >
                                <td class="tw-font-bold">{{ r.no }}</td>
                                <td>
                                  <v-chip
                                    small
                                    :color="r.ok ? 'green' : 'red'"
                                    text-color="white"
                                    class="tw-font-bold"
                                  >
                                    <v-icon left small>
                                      {{ r.ok ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                                    </v-icon>
                                    {{ r.ok ? (r.skipped ? 'SKIP' : 'OK') : 'FAIL' }}
                                  </v-chip>
                                </td>
                                <td class="tw-font-mono tw-text-xs">
                                  <span v-if="r.newCompanyId">{{ r.newCompanyId }}</span>
                                  <span v-else class="tw-text-gray-400">-</span>
                                </td>
                                <td class="tw-text-xs">
                                  <span v-if="r.copiedDocs !== undefined">{{ r.copiedDocs }}</span>
                                  <span v-else>-</span>
                                </td>
                                <td class="tw-text-xs">
                                  <span v-if="r.vendorCount !== undefined">{{ r.vendorCount }}</span>
                                  <span v-else>-</span>
                                </td>
                                <td class="tw-text-xs">
                                  <span v-if="r.skipped" class="tw-text-gray-600">
                                    {{ r.message || 'already done' }}
                                  </span>
                                  <span v-else-if="!r.ok" class="tw-text-red-600">
                                    {{ r.code }}<span v-if="r.error">: {{ r.error }}</span>
                                  </span>
                                  <span v-else class="tw-text-gray-500">-</span>
                                </td>
                              </tr>
                            </tbody>
                          </v-simple-table>
                        </div>
                      </div>

                      <div class="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-mt-5">
                        <v-btn
                          v-if="stepStatuses[8] === 'error'"
                          color="primary"
                          depressed
                          rounded
                          large
                          :disabled="isRunning"
                          @click="runApply"
                        >
                          <v-icon left>mdi-refresh</v-icon>
                          Retry (resume {{ applyRunId ? applyRunId.slice(0, 8) + '…' : '' }})
                        </v-btn>
                        <v-btn
                          v-if="stepStatuses[8] !== 'success'"
                          color="grey darken-2"
                          outlined
                          rounded
                          :disabled="isRunning"
                          @click="cancelRun"
                        >
                          <v-icon left>mdi-close</v-icon>
                          Cancel
                        </v-btn>
                        <v-btn
                          v-if="stepStatuses[8] === 'success'"
                          color="primary"
                          depressed
                          rounded
                          large
                          :to="{ name: 'Company.Settings.CreateDuplicateCompany' }"
                        >
                          <v-icon left>mdi-check</v-icon>
                          Done
                        </v-btn>
                      </div>
                    </v-stepper-content>
                  </v-stepper-items>
                </v-stepper>
              </v-card>

              <!-- ============== ACTIONS ============== -->
              <div
                v-if="!runStarted"
                class="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-mb-4"
              >
                <v-btn
                  outlined
                  color="grey darken-2"
                  :to="{ name: 'Company.Settings.CreateDuplicateCompany' }"
                >
                  Cancel
                </v-btn>
                <v-btn
                  color="primary"
                  depressed
                  large
                  :disabled="!canRun"
                  type="submit"
                >
                  <v-icon left>mdi-play</v-icon>
                  Run
                </v-btn>
              </div>
            </v-container>
            </v-form>
          </v-card-text>

          <div
            v-else
            class="align-center justify-center"
            style="height: 200px; display: flex"
          >
            No Permission
          </div>
        </v-card>

        <!-- Loading skeleton when product list is loading -->
        <v-card v-else class="rounded-xl tw-min-h-80 tw-p-7">
          <v-skeleton-loader type="list-item-two-line" style="width: 400px;" />
          <v-skeleton-loader type="list-item-three-line" style="width: 400px;" />
          <v-skeleton-loader type="list-item-three-line" style="width: 400px;" />
        </v-card>
      </v-container>
    </v-main>
  </Admin>
</template>

<script>
import axios from "axios";
import Admin from "../../components/admin/admin-main-template";
import { mapState } from "vuex";
import firebase from "firebase/app";
import "firebase/auth";
import fc from "@/firebaseConfig";
import { checkImageSmall } from "@/libs/validateImageUpload";
import { uploadToCurrentUserCompanyBucket } from "@/libs/imageUploader";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default {
  name: "CreateDuplicateCompanyConfig",
  components: { Admin },

  computed: {
    ...mapState({
      selectedCompany: (state) => state.companies.selectedCompany,
      isProductListLoading: (state) => state.companies.isProductListLoading,
    }),

    emailErrors() {
      const errors = [];
      const notRegistered = this.form.emails.filter(
        (e) => this.emailStatus[e] === "not-registered"
      );
      const invalid = this.form.emails.filter(
        (e) => this.emailStatus[e] === "invalid"
      );
      if (invalid.length) {
        errors.push("Invalid format: " + invalid.join(", "));
      }
      if (notRegistered.length) {
        errors.push("Not registered: " + notRegistered.join(", "));
      }
      return errors;
    },

    totalCount() {
      const from = Number(this.form.copyFrom) || 0;
      const to = Number(this.form.copyTo) || 0;
      return Math.max(0, to - from + 1);
    },

    copyNumbers() {
      const from = Number(this.form.copyFrom) || 0;
      const to = Number(this.form.copyTo) || 0;
      if (to < from) return [];
      const out = [];
      for (let i = from; i <= to; i++) out.push(i);
      return out;
    },

    visibleCopyNumbers() {
      if (this.totalCount <= 10) return this.copyNumbers;
      if (this.basePathMode !== "manual") return [];
      return this.copyNumbers.slice(0, this.manualVisibleCount);
    },

    basePathFilledCount() {
      return this.copyNumbers.filter(
        (n) => this.foxpro.basePaths[n] && String(this.foxpro.basePaths[n]).trim()
      ).length;
    },

    allBasePathsFilled() {
      return (
        this.copyNumbers.length > 0 &&
        this.basePathFilledCount === this.copyNumbers.length
      );
    },

    allEmailsRegistered() {
      if (!this.form.emails.length) return false;
      return this.form.emails.every(
        (e) => this.emailStatus[e] === "registered"
      );
    },

    canRun() {
      if (this.isRunning) return false;
      if (!this.copyNumbers.length) return false;
      if (!this.allEmailsRegistered) return false;
      if (!this.allBasePathsFilled) return false;
      return true;
    },

    testOkCount() {
      return this.testResults.filter((r) => r.ok).length;
    },

    testFailCount() {
      return Math.max(this.testResults.length - this.testOkCount, 0);
    },

    testAllOk() {
      return (
        this.testResults.length > 0 &&
        this.testOkCount === this.testResults.length
      );
    },

    infoOkCount() {
      return this.infoResults.filter((r) => r.ok).length;
    },

    infoFailCount() {
      return Math.max(this.infoResults.length - this.infoOkCount, 0);
    },

    expenseOkCount() {
      return this.expenseResults.filter((r) => r.ok).length;
    },

    expenseFailCount() {
      return Math.max(this.expenseResults.length - this.expenseOkCount, 0);
    },

    expenseTotalItems() {
      let n = 0;
      for (const r of this.expenseResults) {
        if (r && r.ok) n += Number(r.totalCount) || 0;
      }
      return n;
    },

    expenseTotalMatched() {
      let n = 0;
      for (const r of this.expenseResults) {
        if (r && r.ok) n += Number(r.matchedCount) || 0;
      }
      return n;
    },

    paymentOkCount() {
      return this.paymentResults.filter((r) => r.ok).length;
    },

    paymentFailCount() {
      return Math.max(this.paymentResults.length - this.paymentOkCount, 0);
    },

    paymentTotalItems() {
      let n = 0;
      for (const r of this.paymentResults) {
        if (r && r.ok) n += Number(r.totalCount) || 0;
      }
      return n;
    },

    masterAllLoaded() {
      const m = this.masterRef;
      return !!(m.company && m.vat && m.withholdingTax && m.expense && m.paymentAccount);
    },

    vendorOkCount() {
      return this.vendorResults.filter((r) => r.ok).length;
    },

    vendorFailCount() {
      return Math.max(this.vendorResults.length - this.vendorOkCount, 0);
    },

    vendorTotalItems() {
      let n = 0;
      for (const r of this.vendorResults) {
        if (r && r.ok) n += Number(r.totalCount) || 0;
      }
      return n;
    },

    vendorTotalSkipped() {
      let n = 0;
      for (const r of this.vendorResults) {
        if (r && r.ok) n += Number(r.skippedCount) || 0;
      }
      return n;
    },

    verifyRows() {
      const infoByNo    = byNo(this.infoResults);
      const expenseByNo = byNo(this.expenseResults);
      const paymentByNo = byNo(this.paymentResults);
      const vendorByNo  = byNo(this.vendorResults);

      return this.copyNumbers.map((no) => {
        const info    = infoByNo[no]    || {};
        const expense = expenseByNo[no] || {};
        const payment = paymentByNo[no] || {};
        const vendor  = vendorByNo[no]  || {};

        const issues = [];
        if (!info.ok)                  issues.push("info");
        if (info.ok && !info.taxID)    issues.push("no taxID");
        if (info.ok && !info.nameTH && !info.nameEN) issues.push("no name");
        if (!expense.ok)               issues.push("expense fail");
        if (expense.ok && expense.unmatchedCount > 0) issues.push(expense.unmatchedCount + " unmatched expense");
        if (!payment.ok)               issues.push("payment fail");
        if (payment.ok && payment.totalCount === 0)  issues.push("no payment accts");
        if (!vendor.ok)                issues.push("vendor fail");

        return {
          no: no,
          basePath: this.foxpro.basePaths[no] || "",
          companyName: this.formatName(this.form.namePattern || "", no),
          nameEN: info.nameEN || "",
          nameTH: info.nameTH || "",
          taxID:  info.taxID  || "",
          expenseCount:    expense.ok ? expense.totalCount   : null,
          expenseMatched:  expense.ok ? expense.matchedCount : null,
          expenseUnmatched:expense.ok ? expense.unmatchedCount : null,
          paymentCount:    payment.ok ? payment.totalCount   : null,
          vendorCount:     vendor.ok  ? vendor.totalCount    : null,
          issues:          issues,
          ok:              issues.filter(function (s) { return s.indexOf("fail") !== -1; }).length === 0,
        };
      });

      function byNo(arr) {
        const m = {};
        for (const r of arr || []) {
          if (r && r.no !== undefined) m[r.no] = r;
        }
        return m;
      }
    },

    verifyIssueList() {
      const out = [];
      // Master data missing
      if (this.masterMissing && this.masterMissing.length) {
        out.push({
          severity: "error",
          message: "ขาดเอกสาร master: " + this.masterMissing.join(", "),
        });
      }
      // Per-copy issues — group by issue type, summarize counts
      const counters = {};
      for (const row of this.verifyRows) {
        for (const issue of row.issues) {
          counters[issue] = (counters[issue] || 0) + 1;
        }
      }
      for (const k of Object.keys(counters)) {
        const isError = k.indexOf("fail") !== -1;
        out.push({
          severity: isError ? "error" : "warn",
          message: counters[k] + " base path · " + k,
        });
      }
      // Email resolution issues
      const unresolvedEmails = this.verifyEmails.filter(
        (e) => e.status === "not-found" || e.status === "error"
      );
      if (unresolvedEmails.length > 0) {
        out.push({
          severity: "error",
          message:
            unresolvedEmails.length +
            " email ไม่พบใน Users: " +
            unresolvedEmails.map((e) => e.email).join(", "),
        });
      }
      if (this.verifyEmails.length === 0 && (this.form.emails || []).length > 0) {
        out.push({
          severity: "warn",
          message: "ยังไม่ได้ resolve email (รอ Verify ทำงาน)",
        });
      }
      if ((this.form.emails || []).length === 0) {
        out.push({
          severity: "error",
          message:
            "ไม่มี email ผู้ใช้ — สำเนาบริษัทจะไม่มีใคร login ได้ กรุณาเพิ่ม email ในฟอร์ม",
        });
      }
      out.push({
        severity: "info",
        message: "กด Confirm & Continue เพื่อเริ่มสร้างสำเนาบริษัท — ขั้นตอนนี้จะเขียนข้อมูลจริง",
      });
      return out;
    },

    verifyHasBlocking() {
      return this.verifyIssueList.some(function (i) { return i.severity === "error"; });
    },

    verifyOkRows() {
      return this.verifyRows.filter(function (r) { return r.ok; }).length;
    },

    verifyTotalRows() {
      return this.verifyRows.length;
    },

    applyOkCount() {
      return this.applyResults.filter(function (r) { return r && r.ok; }).length;
    },

    applyFailCount() {
      return Math.max(this.applyResults.length - this.applyOkCount, 0);
    },

    applyElapsedSec() {
      if (!this.applyStartedAt) return 0;
      return Math.round((Date.now() - this.applyStartedAt) / 1000);
    },

    masterDocSummary() {
      const self = this;
      const sizeOf = (doc) => {
        if (!doc) return 0;
        const info = self.pickArrayField(doc);
        return info ? info.value.length : 0;
      };
      const fieldOf = (doc) => {
        if (!doc) return "";
        const info = self.pickArrayField(doc);
        return info ? info.key : "";
      };
      const companyKeys = this.masterRef.company
        ? Object.keys(this.masterRef.company).length
        : 0;
      return [
        { key: "company",        label: "Company (root)", doc: this.masterRef.company,        count: companyKeys,                            arrayField: "(fields)" },
        { key: "vat",            label: "Vat",            doc: this.masterRef.vat,            count: sizeOf(this.masterRef.vat),             arrayField: fieldOf(this.masterRef.vat) },
        { key: "withholdingTax", label: "WithholdingTax", doc: this.masterRef.withholdingTax, count: sizeOf(this.masterRef.withholdingTax),  arrayField: fieldOf(this.masterRef.withholdingTax) },
        { key: "expense",        label: "Expense",        doc: this.masterRef.expense,        count: sizeOf(this.masterRef.expense),         arrayField: fieldOf(this.masterRef.expense) },
        { key: "paymentAccount", label: "PaymentAccount", doc: this.masterRef.paymentAccount, count: sizeOf(this.masterRef.paymentAccount),  arrayField: fieldOf(this.masterRef.paymentAccount) },
      ];
    },

    overallProgressPct() {
      const total = this.steps.length;
      let done = 0;
      let partial = 0;
      this.steps.forEach((s) => {
        const status = this.stepStatuses[s.id];
        if (status === "success") done++;
        else if (status === "running" && s.id === 1 && this.testProgressTotal > 0) {
          partial = this.testProgressDone / this.testProgressTotal;
        }
        else if (status === "running" && s.id === 2 && this.infoProgressTotal > 0) {
          partial = this.infoProgressDone / this.infoProgressTotal;
        }
        else if (status === "running" && s.id === 3 && this.expenseProgressTotal > 0) {
          partial = this.expenseProgressDone / this.expenseProgressTotal;
        }
        else if (status === "running" && s.id === 4 && this.paymentProgressTotal > 0) {
          partial = this.paymentProgressDone / this.paymentProgressTotal;
        }
        else if (status === "running" && s.id === 5 && this.masterLoading) {
          partial = 0.5;
        }
        else if (status === "running" && s.id === 6 && this.vendorProgressTotal > 0) {
          partial = this.vendorProgressDone / this.vendorProgressTotal;
        }
        else if (status === "running" && s.id === 7) {
          partial = 0.5;
        }
        else if (status === "running" && s.id === 8 && this.applyProgressTotal > 0) {
          partial = this.applyProgressDone / this.applyProgressTotal;
        }
      });
      return Math.min(100, Math.round(((done + partial) / total) * 100));
    },

    runStepperHasError() {
      return Object.keys(this.stepStatuses).some(
        (k) => this.stepStatuses[k] === "error"
      );
    },

    runStepperAllDone() {
      return this.steps.every(
        (s) => this.stepStatuses[s.id] === "success"
      );
    },

    runStepperStateClass() {
      if (this.runStepperHasError) return "is-error";
      if (this.runStepperAllDone) return "is-success";
      if (this.isRunning) return "is-running";
      return "is-idle";
    },

    runStepperHeroIcon() {
      if (this.runStepperHasError) return "mdi-alert-octagon";
      if (this.runStepperAllDone) return "mdi-check-decagram";
      if (this.isRunning) return "mdi-cog-sync";
      return "mdi-play-circle";
    },

    runStepperHeroSubtitle() {
      if (this.runStepperHasError) return "พบข้อผิดพลาด — กรุณายกเลิกและแก้ไข";
      if (this.runStepperAllDone) return "เสร็จสมบูรณ์";
      const current = this.steps.find((s) => s.id === this.currentStep);
      const title = current ? current.title : "";
      if (this.isRunning) return "กำลังดำเนินการ — " + title;
      return title;
    },

    namePreview() {
      const pattern = this.form.namePattern || "";
      if (!pattern || !/\{n(?::\d+)?\}/.test(pattern)) return [];
      const from = Number(this.form.copyFrom) || 0;
      const to = Number(this.form.copyTo) || 0;
      if (to < from) return [];
      const max = 5;
      const out = [];
      for (let i = from; i <= to && out.length < max; i++) {
        out.push(this.formatName(pattern, i));
      }
      return out;
    },

    namePreviewTruncated() {
      return this.totalCount > this.namePreview.length;
    },
  },

  data() {
    return {
      items: [
        { text: "ตั้งค่า", disabled: false, to: { name: "Company.Settings" } },
        {
          text: "สร้างสำเนาบริษัท",
          disabled: false,
          to: { name: "Company.Settings.CreateDuplicateCompany" },
        },
        { text: "สร้างสำเนาบริษัทและตั้งค่า", disabled: true },
      ],
      form: {
        companyId: "",
        copyFrom: 1,
        copyTo: 1,
        namePattern: "{n:3}",
        logoFile: null,
        emails: [],
        emailRoles: {},
      },
      roleOptions: [
        { text: "owner", value: "owner" },
        { text: "developer (SLD admin)", value: "developer" },
        { text: "user (read/write docs)", value: "user" },
      ],
      foxpro: {
        apiUrl: "",
        storageMode: "smb",
        networkPath: "",
        shareName: "",
        username: "",
        password: "",
        domain: "",
        basePaths: {},
      },
      showPassword: false,
      isLoadingFoxproDefaults: false,
      foxproDefaultsLoaded: false,
      basePathMode: "import",
      manualVisibleCount: 10,
      logoPreview: "",
      emailInput: "",
      emailStatus: {},
      formValid: false,
      isRunning: false,
      runStarted: false,
      currentStep: 1,
      steps: [
        {
          id: 1,
          title: "Test FoxPro",
          description: "Check every base path",
          icon: "mdi-lan-connect",
        },
        {
          id: 2,
          title: "Read company info",
          description: "Fetch ISINFO from each base path",
          icon: "mdi-domain",
        },
        {
          id: 3,
          title: "Expense mapping",
          description: "Join STMAS × ISACC per base path",
          icon: "mdi-link-variant",
        },
        {
          id: 4,
          title: "Payment mapping",
          description: "Filter GLACC cash/bank accounts",
          icon: "mdi-cash-multiple",
        },
        {
          id: 5,
          title: "Copy master data",
          description: "Vat / WHT / Expense / PaymentAccount",
          icon: "mdi-content-copy",
        },
        {
          id: 6,
          title: "Sync vendors",
          description: "Read APMAS per base path",
          icon: "mdi-account-multiple-plus",
        },
        {
          id: 7,
          title: "Verify structure",
          description: "Review everything before apply",
          icon: "mdi-shield-check",
        },
        {
          id: 8,
          title: "Finish",
          description: "Review completion",
          icon: "mdi-check-all",
        },
      ],
      stepStatuses: { 1: "pending", 2: "pending", 3: "pending", 4: "pending", 5: "pending", 6: "pending", 7: "pending", 8: "pending" },
      testResults: [],
      testGatewayInfo: null,
      testError: "",
      testProgressDone: 0,
      testProgressTotal: 0,
      infoResults: [],
      infoError: "",
      infoProgressDone: 0,
      infoProgressTotal: 0,
      expenseResults: [],
      expenseError: "",
      expenseProgressDone: 0,
      expenseProgressTotal: 0,
      expenseExpanded: {},
      paymentResults: [],
      paymentError: "",
      paymentProgressDone: 0,
      paymentProgressTotal: 0,
      paymentExpanded: {},
      masterRef: {
        company: null,
        vat: null,
        withholdingTax: null,
        expense: null,
        paymentAccount: null,
      },
      masterError: "",
      masterLoading: false,
      masterMissing: [],
      vendorResults: [],
      vendorError: "",
      vendorProgressDone: 0,
      vendorProgressTotal: 0,
      vendorExpanded: {},
      verifyIssues: [],
      verifyEmails: [],
      verifyEmailsLoading: false,
      applyResults: [],
      applyError: "",
      applyProgressDone: 0,
      applyProgressTotal: 0,
      applyRunId: "",
      applyLogoUrl: "",
      applyLogoPath: "",
      applyLogoUploading: false,
      applyStartedAt: 0,
      rules: {
        required: (v) =>
          (v !== null && v !== undefined && v !== "") || "Required",
        minOne: (v) => Number(v) >= 1 || "Must be >= 1",
        endGteStart: (v) =>
          Number(v) >= Number(this.form.copyFrom) ||
          "Copy To must be >= Copy From",
        patternHasN: (v) =>
          (v && /\{n(?::\d+)?\}/.test(String(v))) ||
          "Pattern must contain {n} or {n:N}",
        imageOnly: (v) =>
          !v || /^image\//.test(v.type) || "File must be an image",
        imageMaxSize: (v) =>
          !v || v.size < 5 * 1024 * 1024 || "Image must be < 5MB",
        urlValid: (v) =>
          !v || /^https?:\/\/.+/i.test(String(v)) ||
          "URL must start with http:// or https://",
      },
    };
  },

  created() {
    this.form.companyId = this.$route.params.companyId || "";
    this.loadFoxproDefaults();
  },

  methods: {
    stepItemClass(step) {
      return "is-" + (this.stepStatuses[step.id] || "pending");
    },

    stepStatusIcon(step) {
      const status = this.stepStatuses[step.id];
      if (status === "success") return "mdi-check-circle";
      if (status === "error") return "mdi-alert-circle";
      if (status === "running") return "mdi-progress-clock";
      return step.icon || "mdi-circle-outline";
    },

    stepStatusLabel(step) {
      const status = this.stepStatuses[step.id];
      if (status === "success") return "Done";
      if (status === "error") return "Needs fix";
      if (status === "running") return "Running";
      return "Waiting";
    },

    onEmailsChanged(emails) {
      const cleaned = [];
      emails.forEach((raw) => {
        if (!raw) return;
        const email = String(raw).trim().toLowerCase();
        if (!email) return;
        if (cleaned.indexOf(email) === -1) {
          cleaned.push(email);
        }
      });
      this.form.emails = cleaned;

      cleaned.forEach((email) => {
        if (!this.emailStatus[email]) {
          this.validateEmail(email);
        }
        if (!this.form.emailRoles[email]) {
          this.$set(this.form.emailRoles, email, "owner");
        }
      });

      Object.keys(this.emailStatus).forEach((key) => {
        if (cleaned.indexOf(key) === -1) {
          this.$delete(this.emailStatus, key);
        }
      });
      Object.keys(this.form.emailRoles).forEach((key) => {
        if (cleaned.indexOf(key) === -1) {
          this.$delete(this.form.emailRoles, key);
        }
      });
    },

    setEmailRole(email, role) {
      const valid = ["owner", "developer", "user"];
      const v = valid.indexOf(role) !== -1 ? role : "owner";
      this.$set(this.form.emailRoles, email, v);
    },

    async validateEmail(email) {
      if (!EMAIL_REGEX.test(email)) {
        this.$set(this.emailStatus, email, "invalid");
        return;
      }
      this.$set(this.emailStatus, email, "checking");
      try {
        const methods = await firebase
          .auth()
          .fetchSignInMethodsForEmail(email);
        if (methods && methods.length > 0) {
          this.$set(this.emailStatus, email, "registered");
        } else {
          this.$set(this.emailStatus, email, "not-registered");
        }
      } catch (err) {
        console.error("validateEmail error:", err);
        this.$set(this.emailStatus, email, "invalid");
      }
    },

    chipColor(email) {
      const status = this.emailStatus[email];
      if (status === "registered") return "green";
      if (status === "not-registered") return "red";
      if (status === "invalid") return "orange";
      return "grey";
    },

    setBasePath(n, value) {
      this.$set(this.foxpro.basePaths, n, value);
    },

    async loadFoxproDefaults() {
      const companyId = this.form.companyId;
      if (!companyId) return;
      this.isLoadingFoxproDefaults = true;
      try {
        const snap = await fc.companies
          .doc(companyId)
          .collection("FoxproConfig")
          .doc("config")
          .get();

        let data = null;
        if (snap.exists) {
          data = snap.data();
        } else {
          const legacy = await fc.companies
            .doc(companyId)
            .collection("Databases")
            .where("type", "==", 2)
            .limit(1)
            .get();
          if (!legacy.empty) data = legacy.docs[0].data();
        }

        if (data) {
          this.foxpro.apiUrl = data.apiUrl || "";
          this.foxpro.storageMode = "smb";
          this.foxpro.networkPath = data.networkPath || "";
          this.foxpro.shareName = data.shareName || "";
          this.foxpro.username = data.username || "";
          this.foxpro.password = data.password || "";
          this.foxpro.domain = data.domain || "";
          this.foxproDefaultsLoaded = true;
        }
      } catch (err) {
        console.error("loadFoxproDefaults error:", err);
      } finally {
        this.isLoadingFoxproDefaults = false;
      }
    },

    async runSubmit() {
      const valid = this.$refs.form && this.$refs.form.validate();
      if (!valid) {
        alert("กรุณาตรวจสอบข้อมูลในฟอร์มให้ครบถ้วน");
        return;
      }
      if (!this.allEmailsRegistered) {
        alert("กรุณาใส่อีเมลที่ลงทะเบียนแล้วเท่านั้น");
        return;
      }
      if (!this.allBasePathsFilled) {
        alert(
          "กรุณากรอก Base Path ให้ครบ " +
            this.basePathFilledCount +
            " / " +
            this.copyNumbers.length
        );
        return;
      }

      this.isRunning = true;
      this.runStarted = true;
      this.currentStep = 1;
      this.$set(this.stepStatuses, 1, "running");
      this.$set(this.stepStatuses, 2, "pending");
      this.$set(this.stepStatuses, 3, "pending");
      this.testResults = [];
      this.testGatewayInfo = null;
      this.testError = "";
      this.testProgressDone = 0;
      this.testProgressTotal = this.copyNumbers.length;

      try {
        const allBasePaths = this.copyNumbers.map((n) => ({
          no: n,
          basePath: this.foxpro.basePaths[n] || "",
        }));

        const CHUNK_SIZE = 50;
        const url = fc.cloudFunctionUrl + "apiFoxproTestBasePaths";
        const accumulated = [];
        let firstGateway = null;

        for (let i = 0; i < allBasePaths.length; i += CHUNK_SIZE) {
          const chunk = allBasePaths.slice(i, i + CHUNK_SIZE);
          const body = {
            apiUrl: this.foxpro.apiUrl,
            storageMode: "smb",
            networkPath: this.foxpro.networkPath,
            shareName: this.foxpro.shareName,
            username: this.foxpro.username,
            password: this.foxpro.password,
            domain: this.foxpro.domain,
            basePaths: chunk,
            concurrency: 5,
            skipHealth: i > 0,
          };

          const response = await axios.post(url, body, {
            headers: { "Content-Type": "application/json" },
            timeout: 540000,
            validateStatus: () => true,
          });

          const data = response.data || {};
          if (!data.success) {
            this.testError =
              (data.error || "Test connection failed") +
              (data.code ? " (" + data.code + ")" : "");
            this.testResults = accumulated.slice();
            this.$set(this.stepStatuses, 1, "error");
            return;
          }

          const payload = data.data || {};
          const chunkResults = Array.isArray(payload.results) ? payload.results : [];
          for (const r of chunkResults) accumulated.push(r);
          if (!firstGateway && data.gateway) firstGateway = data.gateway;

          this.testResults = accumulated.slice();
          this.testGatewayInfo = firstGateway;
          this.testProgressDone = accumulated.length;
        }

        const okCount = accumulated.filter((r) => r.ok).length;
        const total = accumulated.length;
        if (okCount === total && total > 0) {
          this.$set(this.stepStatuses, 1, "success");
        } else {
          this.$set(this.stepStatuses, 1, "error");
        }
      } catch (err) {
        console.error("runSubmit error:", err);
        this.testError = (err && err.message) || "Network error";
        this.$set(this.stepStatuses, 1, "error");
      } finally {
        this.isRunning = false;
      }
    },

    goToStep(step) {
      this.currentStep = step;
      if (this.stepStatuses[step] === "pending") {
        this.$set(this.stepStatuses, step, "running");
      }
      if (step === 2 && !this.isRunning) {
        this.runGetCompanyInfo();
      }
      if (step === 3 && !this.isRunning) {
        this.runGetExpenseMapping();
      }
      if (step === 4 && !this.isRunning) {
        this.runGetPaymentMapping();
      }
      if (step === 5 && !this.isRunning) {
        this.runCopyMasterData();
      }
      if (step === 6 && !this.isRunning) {
        this.runSyncVendors();
      }
      if (step === 7 && !this.isRunning) {
        this.runVerifyStructure();
      }
      if (step === 8 && !this.isRunning) {
        this.runApply();
      }
    },

    pickArrayField(doc) {
      if (!doc || typeof doc !== "object") return null;
      const preferred = ["items", "list", "data", "vatList", "whtList", "expenses", "accounts"];
      for (const k of preferred) {
        if (Array.isArray(doc[k])) return { key: k, value: doc[k] };
      }
      for (const k of Object.keys(doc)) {
        if (Array.isArray(doc[k])) return { key: k, value: doc[k] };
      }
      return null;
    },

    cancelRun() {
      this.runStarted = false;
      this.currentStep = 1;
      this.$set(this.stepStatuses, 1, "pending");
      this.$set(this.stepStatuses, 2, "pending");
      this.$set(this.stepStatuses, 3, "pending");
      this.$set(this.stepStatuses, 4, "pending");
      this.$set(this.stepStatuses, 5, "pending");
      this.$set(this.stepStatuses, 6, "pending");
      this.$set(this.stepStatuses, 7, "pending");
      this.$set(this.stepStatuses, 8, "pending");
      this.testResults = [];
      this.testGatewayInfo = null;
      this.testError = "";
      this.testProgressDone = 0;
      this.testProgressTotal = 0;
      this.infoResults = [];
      this.infoError = "";
      this.infoProgressDone = 0;
      this.infoProgressTotal = 0;
      this.expenseResults = [];
      this.expenseError = "";
      this.expenseProgressDone = 0;
      this.expenseProgressTotal = 0;
      this.expenseExpanded = {};
      this.paymentResults = [];
      this.paymentError = "";
      this.paymentProgressDone = 0;
      this.paymentProgressTotal = 0;
      this.paymentExpanded = {};
      this.masterRef = {
        company: null,
        vat: null,
        withholdingTax: null,
        expense: null,
        paymentAccount: null,
      };
      this.masterError = "";
      this.masterLoading = false;
      this.masterMissing = [];
      this.vendorResults = [];
      this.vendorError = "";
      this.vendorProgressDone = 0;
      this.vendorProgressTotal = 0;
      this.vendorExpanded = {};
      this.verifyIssues = [];
      this.verifyEmails = [];
      this.verifyEmailsLoading = false;
      this.applyResults = [];
      this.applyError = "";
      this.applyProgressDone = 0;
      this.applyProgressTotal = 0;
      this.applyRunId = "";
      this.applyLogoUrl = "";
      this.applyLogoPath = "";
      this.applyLogoUploading = false;
      this.applyStartedAt = 0;
    },

    toggleExpenseRow(no) {
      this.$set(this.expenseExpanded, no, !this.expenseExpanded[no]);
    },

    togglePaymentRow(no) {
      this.$set(this.paymentExpanded, no, !this.paymentExpanded[no]);
    },

    toggleVendorRow(no) {
      this.$set(this.vendorExpanded, no, !this.vendorExpanded[no]);
    },

    async runGetCompanyInfo() {
      this.isRunning = true;
      this.$set(this.stepStatuses, 2, "running");
      this.infoResults = [];
      this.infoError = "";
      this.infoProgressDone = 0;
      this.infoProgressTotal = this.copyNumbers.length;

      try {
        const allBasePaths = this.copyNumbers.map((n) => ({
          no: n,
          basePath: this.foxpro.basePaths[n] || "",
        }));

        const CHUNK_SIZE = 50;
        const url = fc.cloudFunctionUrl + "apiFoxproGetCompanyInfoBasePaths";
        const accumulated = [];

        for (let i = 0; i < allBasePaths.length; i += CHUNK_SIZE) {
          const chunk = allBasePaths.slice(i, i + CHUNK_SIZE);
          const body = {
            apiUrl: this.foxpro.apiUrl,
            storageMode: "smb",
            networkPath: this.foxpro.networkPath,
            shareName: this.foxpro.shareName,
            username: this.foxpro.username,
            password: this.foxpro.password,
            domain: this.foxpro.domain,
            basePaths: chunk,
            concurrency: 5,
            skipHealth: true,
          };

          const response = await axios.post(url, body, {
            headers: { "Content-Type": "application/json" },
            timeout: 540000,
            validateStatus: () => true,
          });

          const data = response.data || {};
          if (!data.success) {
            this.infoError =
              (data.error || "Read company info failed") +
              (data.code ? " (" + data.code + ")" : "");
            this.infoResults = accumulated.slice();
            this.$set(this.stepStatuses, 2, "error");
            return;
          }

          const payload = data.data || {};
          const chunkResults = Array.isArray(payload.results) ? payload.results : [];
          for (const r of chunkResults) accumulated.push(r);

          this.infoResults = accumulated.slice();
          this.infoProgressDone = accumulated.length;
        }

        const okCount = accumulated.filter((r) => r.ok).length;
        const total = accumulated.length;
        if (okCount === total && total > 0) {
          this.$set(this.stepStatuses, 2, "success");
        } else {
          this.$set(this.stepStatuses, 2, "error");
        }
      } catch (err) {
        console.error("runGetCompanyInfo error:", err);
        this.infoError = (err && err.message) || "Network error";
        this.$set(this.stepStatuses, 2, "error");
      } finally {
        this.isRunning = false;
      }
    },

    async runGetExpenseMapping() {
      this.isRunning = true;
      this.$set(this.stepStatuses, 3, "running");
      this.expenseResults = [];
      this.expenseError = "";
      this.expenseProgressDone = 0;
      this.expenseProgressTotal = this.copyNumbers.length;
      this.expenseExpanded = {};

      try {
        const allBasePaths = this.copyNumbers.map((n) => ({
          no: n,
          basePath: this.foxpro.basePaths[n] || "",
        }));

        const CHUNK_SIZE = 25;
        const url = fc.cloudFunctionUrl + "apiFoxproGetExpenseMappingBasePaths";
        const accumulated = [];

        for (let i = 0; i < allBasePaths.length; i += CHUNK_SIZE) {
          const chunk = allBasePaths.slice(i, i + CHUNK_SIZE);
          const body = {
            apiUrl: this.foxpro.apiUrl,
            storageMode: "smb",
            networkPath: this.foxpro.networkPath,
            shareName: this.foxpro.shareName,
            username: this.foxpro.username,
            password: this.foxpro.password,
            domain: this.foxpro.domain,
            basePaths: chunk,
            concurrency: 5,
            skipHealth: true,
          };

          const response = await axios.post(url, body, {
            headers: { "Content-Type": "application/json" },
            timeout: 540000,
            validateStatus: () => true,
          });

          const data = response.data || {};
          if (!data.success) {
            this.expenseError =
              (data.error || "Expense mapping failed") +
              (data.code ? " (" + data.code + ")" : "");
            this.expenseResults = accumulated.slice();
            this.$set(this.stepStatuses, 3, "error");
            return;
          }

          const payload = data.data || {};
          const chunkResults = Array.isArray(payload.results) ? payload.results : [];
          for (const r of chunkResults) accumulated.push(r);

          this.expenseResults = accumulated.slice();
          this.expenseProgressDone = accumulated.length;
        }

        const okCount = accumulated.filter((r) => r.ok).length;
        const total = accumulated.length;
        if (okCount === total && total > 0) {
          this.$set(this.stepStatuses, 3, "success");
        } else {
          this.$set(this.stepStatuses, 3, "error");
        }
      } catch (err) {
        console.error("runGetExpenseMapping error:", err);
        this.expenseError = (err && err.message) || "Network error";
        this.$set(this.stepStatuses, 3, "error");
      } finally {
        this.isRunning = false;
      }
    },

    async runGetPaymentMapping() {
      this.isRunning = true;
      this.$set(this.stepStatuses, 4, "running");
      this.paymentResults = [];
      this.paymentError = "";
      this.paymentProgressDone = 0;
      this.paymentProgressTotal = this.copyNumbers.length;
      this.paymentExpanded = {};

      try {
        const allBasePaths = this.copyNumbers.map((n) => ({
          no: n,
          basePath: this.foxpro.basePaths[n] || "",
        }));

        const CHUNK_SIZE = 25;
        const url = fc.cloudFunctionUrl + "apiFoxproGetPaymentMappingBasePaths";
        const accumulated = [];

        for (let i = 0; i < allBasePaths.length; i += CHUNK_SIZE) {
          const chunk = allBasePaths.slice(i, i + CHUNK_SIZE);
          const body = {
            apiUrl: this.foxpro.apiUrl,
            storageMode: "smb",
            networkPath: this.foxpro.networkPath,
            shareName: this.foxpro.shareName,
            username: this.foxpro.username,
            password: this.foxpro.password,
            domain: this.foxpro.domain,
            basePaths: chunk,
            concurrency: 5,
            skipHealth: true,
          };

          const response = await axios.post(url, body, {
            headers: { "Content-Type": "application/json" },
            timeout: 540000,
            validateStatus: () => true,
          });

          const data = response.data || {};
          if (!data.success) {
            this.paymentError =
              (data.error || "Payment mapping failed") +
              (data.code ? " (" + data.code + ")" : "");
            this.paymentResults = accumulated.slice();
            this.$set(this.stepStatuses, 4, "error");
            return;
          }

          const payload = data.data || {};
          const chunkResults = Array.isArray(payload.results) ? payload.results : [];
          for (const r of chunkResults) accumulated.push(r);

          this.paymentResults = accumulated.slice();
          this.paymentProgressDone = accumulated.length;
        }

        const okCount = accumulated.filter((r) => r.ok).length;
        const total = accumulated.length;
        if (okCount === total && total > 0) {
          this.$set(this.stepStatuses, 4, "success");
        } else {
          this.$set(this.stepStatuses, 4, "error");
        }
      } catch (err) {
        console.error("runGetPaymentMapping error:", err);
        this.paymentError = (err && err.message) || "Network error";
        this.$set(this.stepStatuses, 4, "error");
      } finally {
        this.isRunning = false;
      }
    },

    async runCopyMasterData() {
      this.isRunning = true;
      this.$set(this.stepStatuses, 5, "running");
      this.masterError = "";
      this.masterLoading = true;
      this.masterMissing = [];
      this.masterRef = {
        company: null,
        vat: null,
        withholdingTax: null,
        expense: null,
        paymentAccount: null,
      };

      try {
        const companyId = this.form.companyId;
        if (!companyId) {
          this.masterError = "Missing companyId";
          this.$set(this.stepStatuses, 5, "error");
          return;
        }

        const companyRef = fc.companies.doc(companyId);
        const masterCol = companyRef.collection("PurchaseInvoiceMaster");

        const docNames = ["Vat", "WithholdingTax", "Expense", "PaymentAccount"];
        const [companySnap, ...masterSnaps] = await Promise.all([
          companyRef.get(),
          ...docNames.map((n) => masterCol.doc(n).get()),
        ]);

        const missing = [];
        if (!companySnap.exists) missing.push("Company");
        const companyData = companySnap.exists ? (companySnap.data() || {}) : null;

        const data = {};
        masterSnaps.forEach((snap, idx) => {
          const name = docNames[idx];
          if (!snap.exists) {
            missing.push(name);
            data[name] = null;
          } else {
            data[name] = snap.data() || {};
          }
        });

        this.masterRef = {
          company: companyData,
          vat: data.Vat,
          withholdingTax: data.WithholdingTax,
          expense: data.Expense,
          paymentAccount: data.PaymentAccount,
        };
        this.masterMissing = missing;

        if (missing.length > 0) {
          this.masterError =
            "Reference docs missing: " + missing.join(", ");
          this.$set(this.stepStatuses, 5, "error");
          return;
        }

        this.$set(this.stepStatuses, 5, "success");
      } catch (err) {
        console.error("runCopyMasterData error:", err);
        this.masterError = (err && err.message) || "Read error";
        this.$set(this.stepStatuses, 5, "error");
      } finally {
        this.masterLoading = false;
        this.isRunning = false;
      }
    },

    async runSyncVendors() {
      this.isRunning = true;
      this.$set(this.stepStatuses, 6, "running");
      this.vendorResults = [];
      this.vendorError = "";
      this.vendorProgressDone = 0;
      this.vendorProgressTotal = this.copyNumbers.length;
      this.vendorExpanded = {};

      try {
        const allBasePaths = this.copyNumbers.map((n) => ({
          no: n,
          basePath: this.foxpro.basePaths[n] || "",
        }));

        const CHUNK_SIZE = 10;
        const url = fc.cloudFunctionUrl + "apiFoxproGetVendorsBasePaths";
        const accumulated = [];

        for (let i = 0; i < allBasePaths.length; i += CHUNK_SIZE) {
          const chunk = allBasePaths.slice(i, i + CHUNK_SIZE);
          const body = {
            apiUrl: this.foxpro.apiUrl,
            storageMode: "smb",
            networkPath: this.foxpro.networkPath,
            shareName: this.foxpro.shareName,
            username: this.foxpro.username,
            password: this.foxpro.password,
            domain: this.foxpro.domain,
            basePaths: chunk,
            concurrency: 5,
            skipHealth: true,
          };

          const response = await axios.post(url, body, {
            headers: { "Content-Type": "application/json" },
            timeout: 540000,
            validateStatus: () => true,
          });

          const data = response.data || {};
          if (!data.success) {
            this.vendorError =
              (data.error || "Sync vendors failed") +
              (data.code ? " (" + data.code + ")" : "");
            this.vendorResults = accumulated.slice();
            this.$set(this.stepStatuses, 6, "error");
            return;
          }

          const payload = data.data || {};
          const chunkResults = Array.isArray(payload.results) ? payload.results : [];
          for (const r of chunkResults) accumulated.push(r);

          this.vendorResults = accumulated.slice();
          this.vendorProgressDone = accumulated.length;
        }

        const okCount = accumulated.filter((r) => r.ok).length;
        const total = accumulated.length;
        if (okCount === total && total > 0) {
          this.$set(this.stepStatuses, 6, "success");
        } else {
          this.$set(this.stepStatuses, 6, "error");
        }
      } catch (err) {
        console.error("runSyncVendors error:", err);
        this.vendorError = (err && err.message) || "Network error";
        this.$set(this.stepStatuses, 6, "error");
      } finally {
        this.isRunning = false;
      }
    },

    detectArrayField(doc) {
      const info = this.pickArrayField(doc);
      return info ? info.key : "items";
    },

    async runApply() {
      this.isRunning = true;
      this.$set(this.stepStatuses, 8, "running");
      this.applyResults = [];
      this.applyError = "";
      this.applyProgressDone = 0;
      this.applyProgressTotal = this.copyNumbers.length;
      this.applyStartedAt = Date.now();

      try {
        // ----- 1. Upload logo (if any) -----
        if (this.form.logoFile && !this.applyLogoUrl) {
          this.applyLogoUploading = true;
          try {
            const uploaded = await uploadToCurrentUserCompanyBucket(
              this.form.logoFile,
              this.form.companyId,
              "duplicate_logos",
              null
            );
            this.applyLogoUrl  = uploaded.imageUrl    || "";
            this.applyLogoPath = uploaded.storagePath || "";
          } catch (upErr) {
            console.error("logo upload failed:", upErr);
            this.applyError = "Logo upload failed: " + (upErr && upErr.message);
            this.$set(this.stepStatuses, 8, "error");
            return;
          } finally {
            this.applyLogoUploading = false;
          }
        }

        // ----- 2. Build per-copy payloads -----
        const infoByNo    = {};
        const expenseByNo = {};
        const paymentByNo = {};
        const vendorByNo  = {};
        for (const r of this.infoResults)    if (r && r.no !== undefined) infoByNo[r.no] = r;
        for (const r of this.expenseResults) if (r && r.no !== undefined) expenseByNo[r.no] = r;
        for (const r of this.paymentResults) if (r && r.no !== undefined) paymentByNo[r.no] = r;
        for (const r of this.vendorResults)  if (r && r.no !== undefined) vendorByNo[r.no] = r;

        const allCopies = this.copyNumbers.map((n) => {
          const info    = infoByNo[n]    || {};
          const expense = expenseByNo[n] || {};
          const payment = paymentByNo[n] || {};
          const vendor  = vendorByNo[n]  || {};
          return {
            no:           n,
            basePath:     this.foxpro.basePaths[n] || "",
            companyName:  this.formatName(this.form.namePattern || "", n),
            nameEN:       info.nameEN || "",
            nameTH:       info.nameTH || "",
            taxID:        info.taxID  || "",
            expenseItems: Array.isArray(expense.items) ? expense.items : [],
            paymentItems: Array.isArray(payment.items) ? payment.items : [],
            vendorItems:  Array.isArray(vendor.items)  ? vendor.items  : [],
          };
        });

        const roles = this.form.emailRoles || {};
        const users = this.verifyEmails
          .filter(function (e) { return e.status === "ok" && e.uid; })
          .map((e) => {
            const u = e.userData || {};
            return {
              uid:              e.uid,
              email:            e.email,
              role:             roles[e.email] || "owner",
              nameFirstTH:      u.nameFirstTH || "",
              nameLastTH:       u.nameLastTH  || "",
              nameFirstEN:      u.nameFirstEN || "",
              nameLastEN:       u.nameLastEN  || "",
              imageProfilePath: u.imageProfilePath || "",
              imageProfileUrl:  u.imageProfileUrl  || "",
              phone:            u.phone     != null ? u.phone     : null,
              pageLimit:        u.pageLimit != null ? u.pageLimit : 10,
            };
          });

        const foxproShared = {
          apiUrl:       this.foxpro.apiUrl,
          storageMode:  "smb",
          networkPath:  this.foxpro.networkPath,
          shareName:    this.foxpro.shareName,
          username:     this.foxpro.username,
          password:     this.foxpro.password,
          domain:       this.foxpro.domain,
        };

        const expenseField = this.detectArrayField(this.masterRef.expense);
        const paymentField = this.detectArrayField(this.masterRef.paymentAccount);

        // ----- 3. Loop chunks -----
        const CHUNK_SIZE = 3;
        const url = fc.cloudFunctionUrl + "apiCreateDuplicateCompanies";
        const accumulated = [];

        for (let i = 0; i < allCopies.length; i += CHUNK_SIZE) {
          const chunk = allCopies.slice(i, i + CHUNK_SIZE);
          const body = {
            sourceCompanyId:    this.form.companyId,
            runId:              this.applyRunId,
            totalPlanned:       allCopies.length,
            copies:             chunk,
            users:              users,
            foxproConfig:       foxproShared,
            logoUrl:            this.applyLogoUrl,
            logoPath:           this.applyLogoPath,
            expenseArrayField:  expenseField,
            paymentArrayField:  paymentField,
          };

          const response = await axios.post(url, body, {
            headers: { "Content-Type": "application/json" },
            timeout: 540000,
            validateStatus: () => true,
          });

          const data = response.data || {};
          if (!data.success) {
            this.applyError =
              (data.error || "Apply failed") +
              (data.code ? " (" + data.code + ")" : "");
            this.applyResults = accumulated.slice();
            this.$set(this.stepStatuses, 8, "error");
            return;
          }

          const payload = data.data || {};
          if (payload.runId && !this.applyRunId) this.applyRunId = payload.runId;
          const chunkResults = Array.isArray(payload.results) ? payload.results : [];
          for (const r of chunkResults) accumulated.push(r);

          this.applyResults = accumulated.slice();
          this.applyProgressDone = accumulated.length;
        }

        const okCount = accumulated.filter(function (r) { return r && r.ok; }).length;
        const total = accumulated.length;
        if (okCount === total && total > 0) {
          this.$set(this.stepStatuses, 8, "success");
        } else {
          this.$set(this.stepStatuses, 8, "error");
        }
      } catch (err) {
        console.error("runApply error:", err);
        this.applyError = (err && err.message) || "Network error";
        this.$set(this.stepStatuses, 8, "error");
      } finally {
        this.isRunning = false;
      }
    },

    async runVerifyStructure() {
      this.isRunning = true;
      this.$set(this.stepStatuses, 7, "running");
      this.verifyEmailsLoading = true;
      try {
        const emails = (this.form.emails || []).map((e) => String(e || "").trim()).filter(Boolean);
        const dedup = Array.from(new Set(emails.map((e) => e.toLowerCase())));

        this.verifyEmails = dedup.map((email) => ({
          email: email,
          uid: null,
          name: "",
          source: "",
          userData: null,
          status: "pending",
          error: "",
        }));

        if (dedup.length > 0) {
          try {
            const url = fc.cloudFunctionUrl + "apiResolveUserByEmail";
            const response = await axios.post(
              url,
              { emails: dedup },
              {
                headers: { "Content-Type": "application/json" },
                timeout: 60000,
                validateStatus: () => true,
              }
            );
            const data = response.data || {};
            if (data.success && data.data && Array.isArray(data.data.results)) {
              const byEmail = {};
              for (const r of data.data.results) {
                if (r && r.email) byEmail[r.email] = r;
              }
              for (const entry of this.verifyEmails) {
                const r = byEmail[entry.email];
                if (!r) {
                  entry.status = "error";
                  entry.error = "no result returned";
                  continue;
                }
                if (r.status === "ok") {
                  entry.uid = r.uid;
                  entry.userData = r.userData || {};
                  entry.name = r.name || "";
                  entry.source = r.source || "";
                  entry.status = "ok";
                } else if (r.status === "not-found") {
                  entry.status = "not-found";
                } else {
                  entry.status = "error";
                  entry.error = r.error || "lookup failed";
                }
              }
            } else {
              for (const entry of this.verifyEmails) {
                entry.status = "error";
                entry.error =
                  (data && data.error) || "resolve api failed";
              }
            }
          } catch (apiErr) {
            console.error("apiResolveUserByEmail error:", apiErr);
            for (const entry of this.verifyEmails) {
              entry.status = "error";
              entry.error = (apiErr && apiErr.message) || "network error";
            }
          }
        }

        // Force reactivity for Vue 2 — replace the array
        this.verifyEmails = this.verifyEmails.slice();
        this.verifyIssues = this.verifyIssueList.slice();

        if (this.verifyHasBlocking) {
          this.$set(this.stepStatuses, 7, "error");
        } else {
          this.$set(this.stepStatuses, 7, "success");
        }
      } catch (err) {
        console.error("runVerifyStructure error:", err);
        this.$set(this.stepStatuses, 7, "error");
      } finally {
        this.verifyEmailsLoading = false;
        this.isRunning = false;
      }
    },

    buildFoxproPayload() {
      return {
        apiUrl: this.foxpro.apiUrl,
        storageMode: "smb",
        networkPath: this.foxpro.networkPath,
        shareName: this.foxpro.shareName,
        username: this.foxpro.username,
        password: this.foxpro.password,
        domain: this.foxpro.domain,
        basePaths: this.copyNumbers.map((n) => ({
          no: n,
          basePath: this.foxpro.basePaths[n] || "",
        })),
      };
    },

    addMoreManualRows() {
      this.manualVisibleCount = Math.min(
        this.manualVisibleCount + 10,
        this.totalCount
      );
    },

    triggerBasePathImport() {
      if (this.$refs.basePathFileInput) {
        this.$refs.basePathFileInput.value = "";
        this.$refs.basePathFileInput.click();
      }
    },

    downloadBasePathTemplate() {
      const header = "copyNo,name,basePath";
      const rows = this.copyNumbers.map((n) => {
        const name = this.formatName(this.form.namePattern || "", n);
        return `${n},${name},`;
      });
      const csv = [header].concat(rows).join("\n");
      const blob = new Blob(["\uFEFF" + csv], {
        type: "text/csv;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `base-path-template-${this.form.companyId || "company"}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },

    onBasePathImport(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = String(e.target.result || "").replace(/^\uFEFF/, "");
          const lines = text.split(/\r?\n/).filter((l) => l.trim().length);
          if (!lines.length) {
            alert("ไฟล์ว่างเปล่า");
            return;
          }
          let startIdx = 0;
          const first = lines[0].toLowerCase();
          if (first.indexOf("copyno") !== -1 || first.indexOf("basepath") !== -1) {
            startIdx = 1;
          }
          const valid = new Set(this.copyNumbers);
          let imported = 0;
          let skipped = 0;
          for (let i = startIdx; i < lines.length; i++) {
            const cols = this.parseCsvLine(lines[i]);
            if (!cols.length) continue;
            const n = Number(cols[0]);
            const basePath = (cols[2] !== undefined ? cols[2] : cols[1]) || "";
            if (!valid.has(n)) {
              skipped++;
              continue;
            }
            this.$set(this.foxpro.basePaths, n, String(basePath).trim());
            imported++;
          }
          alert(
            `Import สำเร็จ ${imported} รายการ` +
              (skipped ? ` (ข้าม ${skipped} รายการ - copyNo อยู่นอกช่วง)` : "")
          );
        } catch (err) {
          console.error("Import error:", err);
          alert("Import ล้มเหลว: " + err.message);
        }
      };
      reader.readAsText(file);
    },

    parseCsvLine(line) {
      const out = [];
      let cur = "";
      let inQuote = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (inQuote) {
          if (ch === '"' && line[i + 1] === '"') {
            cur += '"';
            i++;
          } else if (ch === '"') {
            inQuote = false;
          } else {
            cur += ch;
          }
        } else if (ch === '"') {
          inQuote = true;
        } else if (ch === ",") {
          out.push(cur);
          cur = "";
        } else {
          cur += ch;
        }
      }
      out.push(cur);
      return out;
    },

    formatName(pattern, n) {
      return String(pattern).replace(/\{n(?::(\d+))?\}/g, (m, pad) => {
        const s = String(n);
        const width = pad ? Number(pad) : 0;
        return width > s.length ? s.padStart(width, "0") : s;
      });
    },

    onLogoSelected(file) {
      if (this.logoPreview) {
        URL.revokeObjectURL(this.logoPreview);
        this.logoPreview = "";
      }
      if (!file) return;
      if (!/^image\//.test(file.type)) return;
      checkImageSmall(file).then((tooSmall) => {
        if (tooSmall) {
          alert("width lower 640 pixels. please select new image.");
          this.form.logoFile = null;
          return;
        }
        this.logoPreview = URL.createObjectURL(file);
      });
    },
  },

  beforeDestroy() {
    if (this.logoPreview) URL.revokeObjectURL(this.logoPreview);
  },
};
</script>

<style lang="scss" scoped>
.run-stepper-card {
  overflow: hidden;
  border: 1px solid #dfe5ec;
  border-radius: 8px !important;
  background: #fff;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08) !important;
  transition: border-color 0.25s ease, box-shadow 0.25s ease;

  &.is-running {
    border-color: #8bb8e8;
    box-shadow: 0 12px 34px rgba(25, 118, 210, 0.14) !important;
  }
  &.is-success {
    border-color: #86c991;
    box-shadow: 0 12px 34px rgba(46, 125, 50, 0.12) !important;
  }
  &.is-error {
    border-color: #ef9a9a;
    box-shadow: 0 12px 34px rgba(198, 40, 40, 0.12) !important;
  }
}

.run-stepper-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 20px 24px;
  background: #fbfcfe;
  color: #1f2933;
  border-bottom: 1px solid #edf1f6;

  .run-stepper-hero-icon {
    background: #e8f1fb !important;
    border: 1px solid #cfe0f3;
    color: #1565c0 !important;

    .v-icon {
      color: #1565c0 !important;
    }
  }
  .run-stepper-hero-chip {
    background: #eef4fb !important;
    border: 1px solid #d4e2f2;
    color: #1f4e79 !important;

    .v-icon {
      color: #1f4e79 !important;
    }
  }
}

.run-stepper-hero .tw-text-white {
  color: #1f2933 !important;
}

.run-stepper-hero .tw-opacity-80 {
  color: #667085 !important;
  opacity: 1 !important;
}

.run-stepper-hero > .v-progress-linear {
  flex: 0 0 220px;
  max-width: 260px;
  margin-top: 0 !important;
}

.run-stepper-metrics {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  padding: 16px 24px;
  background: #fff;
  border-bottom: 1px solid #edf1f6;
}

.run-stepper-metric {
  min-width: 0;
  padding: 12px 14px;
  border: 1px solid #e4e9f0;
  border-radius: 8px;
  background: #fafbfc;

  span {
    display: block;
    margin-bottom: 4px;
    color: #667085;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
  }

  strong {
    color: #1f2933;
    font-size: 20px;
    line-height: 1.1;

    &.is-ok {
      color: #2e7d32;
    }

    &.is-fail {
      color: #c62828;
    }
  }
}

.run-stepper-track {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  padding: 18px 24px;
  background: #f8fafc;
  border-bottom: 1px solid #edf1f6;
}

.run-stepper-track-item {
  position: relative;
  display: grid;
  grid-template-columns: 38px minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  min-width: 0;
  min-height: 70px;
  padding: 12px;
  border: 1px solid #e1e7ee;
  border-radius: 8px;
  background: #fff;

  &:not(.is-last)::after {
    content: "";
    position: absolute;
    top: 50%;
    right: -15px;
    width: 14px;
    height: 2px;
    background: #cfd8e3;
    transform: translateY(-50%);
  }

  &.is-current {
    border-color: #90b7e6;
    box-shadow: 0 6px 16px rgba(25, 118, 210, 0.1);
  }

  &.is-success {
    border-color: #b7d9bd;
    background: #f8fcf9;

    &:not(.is-last)::after {
      background: #66bb6a;
    }
  }

  &.is-error {
    border-color: #f2b8b5;
    background: #fffafa;
  }
}

.run-stepper-step-marker {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #eef2f6;
  color: #7a8796;
}

.run-stepper-track-item.is-running .run-stepper-step-marker {
  background: #e8f1fb;
  color: #1565c0;
}

.run-stepper-track-item.is-success .run-stepper-step-marker {
  background: #e8f5e9;
  color: #2e7d32;
}

.run-stepper-track-item.is-error .run-stepper-step-marker {
  background: #ffebee;
  color: #c62828;
}

.run-stepper-step-copy {
  min-width: 0;
}

.run-stepper-step-title {
  color: #1f2933;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.25;
}

.run-stepper-step-desc {
  margin-top: 3px;
  color: #667085;
  font-size: 12px;
  line-height: 1.25;
}

.run-stepper-step-chip {
  color: #4b5563 !important;
  background: #eef2f6 !important;

  &.is-running {
    color: #1565c0 !important;
    background: #e8f1fb !important;
  }

  &.is-success {
    color: #2e7d32 !important;
    background: #e8f5e9 !important;
  }

  &.is-error {
    color: #c62828 !important;
    background: #ffebee !important;
  }
}

.run-stepper-body {
  background: transparent !important;
}

.run-stepper-header {
  display: none;
}

.run-stepper-body ::v-deep .v-stepper__content {
  padding: 20px 24px 24px;
}

.run-stepper-status {
  border-radius: 8px;
  padding: 16px 18px;
  border: 1px solid transparent;

  &.running {
    background: #f4f8fd;
    border-color: #cfe0f3;
  }
  &.success {
    background: #f3fbf5;
    border-color: #c6e4ca;
  }
  &.error {
    background: #fff6f6;
    border-color: #f5c2c0;
  }
  &.info {
    background: #f4f8fd;
    border-color: #cfe0f3;
  }
}

.run-stepper-results-panel {
  overflow: hidden;
  border: 1px solid #e1e7ee;
  border-radius: 8px;
  background: #fff;
}

.run-stepper-results-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  background: #fafbfc;
  border-bottom: 1px solid #e8edf3;
}

.run-stepper-results-title {
  color: #1f2933;
  font-size: 14px;
  font-weight: 700;
}

.run-stepper-results-subtitle {
  color: #667085;
  font-size: 12px;
}

.run-stepper-results-chip {
  color: #475467 !important;
  background: #eef2f6 !important;
}

.run-stepper-table-wrap {
  overflow-x: auto;
}

.run-stepper-base-path {
  max-width: 260px;
  overflow: hidden;
  color: #344054;
  font-family: Consolas, "Liberation Mono", monospace;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 960px) {
  .run-stepper-hero {
    align-items: stretch;
    flex-direction: column;
  }

  .run-stepper-hero > .v-progress-linear {
    flex-basis: auto;
    max-width: none;
  }

  .run-stepper-metrics,
  .run-stepper-track {
    grid-template-columns: 1fr;
  }

  .run-stepper-track-item {
    grid-template-columns: 36px minmax(0, 1fr);

    &:not(.is-last)::after {
      display: none;
    }
  }

  .run-stepper-step-chip {
    grid-column: 2;
    justify-self: start;
  }
}

.run-stepper-table-head {
  background: #f5f7fb;
  th {
    font-weight: 700 !important;
    color: #455a64 !important;
    text-transform: uppercase;
    font-size: 11px !important;
    letter-spacing: 0.4px;
  }
}

.run-stepper-row-ok {
  background: rgba(76, 175, 80, 0.04);
}
.run-stepper-row-fail {
  background: rgba(244, 67, 54, 0.05);
}
.run-stepper-row-warn {
  background: rgba(255, 152, 0, 0.06);
}
.run-stepper-detail-row > td {
  background: #fafbff;
  border-top: 1px dashed #c5cae9 !important;
}
.run-stepper-detail-wrap {
  padding: 12px 16px 16px 16px;
}
.run-stepper-detail-title {
  font-size: 12px;
  font-weight: 700;
  color: #3949ab;
  letter-spacing: 0.3px;
  margin-bottom: 8px;
  text-transform: uppercase;
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.fade-slide-enter,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(6px);
}
</style>
